/**
 * PadLab's offline renderer and the DSP library the rest of the PWA is built on.
 *
 * Everything the user can hear that is *not* a live pad passes through here: the wave
 * editor's A/B preview, a bounce, a stem, a full mixdown, and the starter sounds
 * `synth.ts` generates. There is exactly one implementation of each operation, so a
 * preview and an export cannot drift apart.
 *
 * Three deliberate choices shape the file.
 *
 * **The edit renderer is direct `Float32Array` DSP, not a graph.** `renderEdit` applies a
 * `SampleEdit` in the same contractual order as `RenderService.swift` on iOS — trim,
 * silence, reverse, rate, pitch, normalise, gain, envelope, fades, loop crossfade,
 * effects, pan, resample. A Web Audio graph cannot express that order (it has no reverse,
 * no non-realtime normalise, and its `playbackRate` is a resample rather than a stretch),
 * and a project rendered on an iPhone and on the web has to sound the same.
 *
 * **The mixdown is an `OfflineAudioContext`.** Once every hit has been rendered to a
 * buffer, assembling them is exactly what an `OfflineAudioContext` is for: sample-accurate
 * `start(when)`, equal-power panning and summing, all on the browser's own rendering
 * thread instead of a main-thread loop.
 *
 * **Long passes yield.** Nothing here may block the main thread — `OfflineAudioContext` is
 * not exposed to workers in any current browser, so this code runs on the main thread and
 * has to give it back. Every long loop is driven through `forEachBlock`, which hands
 * control to the event loop whenever the current slice has run past its budget. The result
 * is a render that keeps the UI at 60 fps while it works.
 */

import { createLogger } from '../core/log';
import {
  automationValueAtBeat,
  clamp,
  clampSampleEdit,
  effectChainActiveNodes,
  effectNodeValue,
  fadeCurveGain,
  gridCapacity,
  patternLengthInBeats,
  projectAllPads,
  trackActiveStepCount,
  trackLeadingStepIndex,
  trackStepAtGlobalPosition,
  type AmpEnvelope,
  type AutomationLane,
  type EffectChain,
  type EffectNode,
  type FadeCurve,
  type Pad,
  type Pattern,
  type Project,
  type RetriggerBehaviour,
  type SampleAsset,
  type SampleEdit,
  type UUID,
} from '../model/types';

const log = createLogger('render');

// MARK: - Types

/** Deinterleaved audio. The unit every function in this module speaks. */
export interface RenderedAudio {
  readonly channels: Float32Array[];
  readonly sampleRate: number;
}

export interface StemRender {
  readonly padID: UUID;
  readonly name: string;
  readonly audio: RenderedAudio;
}

/** Thrown when the user cancels. Never shown as an error. */
export class RenderCancelledError extends Error {
  constructor() {
    super('Render cancelled.');
    this.name = 'RenderCancelledError';
  }
}

/**
 * A render that could not finish. `message` is a finished sentence for the banner: what
 * happened and what to do about it, in the wording style of `AudioEngineError`.
 */
export class RenderError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RenderError';
  }
}

/** A format this browser cannot write. The message names the format that does work. */
export class ExportFormatUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ExportFormatUnavailableError';
  }
}

// MARK: - Cooperative scheduling

const MESSAGE_CHANNEL_AVAILABLE = typeof MessageChannel === 'function';

/**
 * Hands the main thread back for one turn. `scheduler.yield` where it exists (Chrome 129+)
 * because it resumes ahead of ordinary tasks; a `MessageChannel` round trip otherwise,
 * which is a real macrotask rather than the 4 ms-clamped `setTimeout(0)`.
 */
function yieldToEventLoop(): Promise<void> {
  const scheduler = (globalThis as { scheduler?: { yield?: () => Promise<void> } }).scheduler;
  if (scheduler !== undefined && typeof scheduler.yield === 'function') {
    return scheduler.yield();
  }
  if (MESSAGE_CHANNEL_AVAILABLE) {
    return new Promise<void>((resolve) => {
      const channel = new MessageChannel();
      channel.port1.onmessage = () => {
        channel.port1.close();
        resolve();
      };
      channel.port2.postMessage(0);
    });
  }
  return new Promise<void>((resolve) => setTimeout(resolve, 0));
}

export interface RenderBudgetOptions {
  /** How long a single uninterrupted slice may run. One frame at 60 fps is 16.7 ms. */
  sliceMs?: number;
  signal?: AbortSignal;
  onProgress?: (fraction: number) => void;
}

/**
 * The render's share of the main thread, plus its cancellation and progress channel.
 *
 * `tick()` is called between units of work. It returns an already-resolved promise while
 * the slice still has time — so the common case costs one microtask — and yields when it
 * does not.
 */
export class RenderBudget {
  private readonly sliceMs: number;
  private readonly signal: AbortSignal | undefined;
  private readonly onProgress: ((fraction: number) => void) | undefined;
  private sliceStart = performance.now();
  private lastReported = -1;

  constructor(options: RenderBudgetOptions = {}) {
    this.sliceMs = options.sliceMs ?? 8;
    this.signal = options.signal;
    this.onProgress = options.onProgress;
  }

  throwIfCancelled(): void {
    if (this.signal?.aborted === true) throw new RenderCancelledError();
  }

  async tick(): Promise<void> {
    this.throwIfCancelled();
    if (performance.now() - this.sliceStart < this.sliceMs) return;
    await yieldToEventLoop();
    this.throwIfCancelled();
    this.sliceStart = performance.now();
  }

  /** Reports at whole-percent granularity; every call may cost a UI update. */
  report(fraction: number): void {
    if (this.onProgress === undefined) return;
    const clamped = clamp(fraction, 0, 1);
    const percent = Math.round(clamped * 100);
    if (percent === this.lastReported) return;
    this.lastReported = percent;
    this.onProgress(clamped);
  }
}

/**
 * Runs `body` over a buffer in blocks, yielding between them. State kept in the closure
 * survives across blocks, which is what lets a filter or a delay line be processed this
 * way without seams.
 */
async function forEachBlock(
  frames: number,
  blockSize: number,
  budget: RenderBudget | undefined,
  body: (from: number, to: number) => void,
): Promise<void> {
  for (let from = 0; from < frames; from += blockSize) {
    body(from, Math.min(frames, from + blockSize));
    if (budget !== undefined) await budget.tick();
  }
}

const BLOCK = 16384;

// MARK: - Level and pan

export function dbToLinear(db: number): number {
  return Math.pow(10, db / 20);
}

export function linearToDB(linear: number): number {
  return linear <= 1e-9 ? -180 : 20 * Math.log10(linear);
}

/**
 * Volume fader taper. A linear 0…1 slider sounds wrong — the top half barely moves — so
 * every fader in PadLab, on both platforms, is cubed.
 */
export function faderTaper(position: number): number {
  const p = clamp(position, 0, 1);
  return p * p * p;
}

/** Constant-power pan. -1 hard left, 0 centre, +1 hard right. */
export function panGains(pan: number): { left: number; right: number } {
  const p = clamp(pan, -1, 1);
  const angle = ((p + 1) * Math.PI) / 4;
  return { left: Math.cos(angle), right: Math.sin(angle) };
}

export function semitonesToRate(semitones: number): number {
  return Math.pow(2, semitones / 12);
}

// MARK: - Determinism
//
// Probability, glitch and synth noise all have to be reproducible: "surprise me" then
// "undo" then "redo" must give the same sound, and a bounce must match the preview it was
// auditioned against. Everything random in PadLab comes from this generator.
//
// It is splitmix64, bit-for-bit the same as `SeededRandom` in `DSPPrimitives.swift`, built
// out of 32-bit halves because JavaScript has no unsigned 64-bit integer that is cheap
// enough for an inner loop. Matching the native stream exactly means a pattern with
// probability steps renders identically on both platforms.

const SPLITMIX_GAMMA_HI = 0x9e3779b9;
const SPLITMIX_GAMMA_LO = 0x7f4a7c15;

function mul64(aHi: number, aLo: number, bHi: number, bLo: number): { hi: number; lo: number } {
  const a0 = aLo & 0xffff;
  const a1 = aLo >>> 16;
  const b0 = bLo & 0xffff;
  const b1 = bLo >>> 16;

  const p00 = a0 * b0;
  let mid = (p00 >>> 16) + a1 * b0;
  let carry = mid >>> 16;
  mid = (mid & 0xffff) + a0 * b1;
  carry += mid >>> 16;

  const lo = (((mid & 0xffff) << 16) | (p00 & 0xffff)) >>> 0;
  const hi = (carry + a1 * b1 + Math.imul(aHi, bLo) + Math.imul(aLo, bHi)) >>> 0;
  return { hi, lo };
}

function add64(aHi: number, aLo: number, bHi: number, bLo: number): { hi: number; lo: number } {
  const lo = (aLo >>> 0) + (bLo >>> 0);
  const hi = (aHi + bHi + (lo > 0xffffffff ? 1 : 0)) >>> 0;
  return { hi, lo: lo >>> 0 };
}

/** Logical right shift of a 64-bit value held as two 32-bit halves. 0 < bits < 32. */
function shr64(hi: number, lo: number, bits: number): { hi: number; lo: number } {
  return { hi: hi >>> bits, lo: ((lo >>> bits) | (hi << (32 - bits))) >>> 0 };
}

export class SeededRandom {
  private hi: number;
  private lo: number;

  constructor(seedHi: number, seedLo: number) {
    // Any non-zero state works; splitmix keeps adjacent seeds from producing similar runs.
    if ((seedHi >>> 0) === 0 && (seedLo >>> 0) === 0) {
      this.hi = SPLITMIX_GAMMA_HI;
      this.lo = SPLITMIX_GAMMA_LO;
    } else {
      this.hi = seedHi >>> 0;
      this.lo = seedLo >>> 0;
    }
  }

  private next(): { hi: number; lo: number } {
    const state = add64(this.hi, this.lo, SPLITMIX_GAMMA_HI, SPLITMIX_GAMMA_LO);
    this.hi = state.hi;
    this.lo = state.lo;

    let z = state;
    let shifted = shr64(z.hi, z.lo, 30);
    z = mul64(z.hi ^ shifted.hi, (z.lo ^ shifted.lo) >>> 0, 0xbf58476d, 0x1ce4e5b9);
    shifted = shr64(z.hi, z.lo, 27);
    z = mul64(z.hi ^ shifted.hi, (z.lo ^ shifted.lo) >>> 0, 0x94d049bb, 0x133111eb);
    shifted = shr64(z.hi, z.lo, 31);
    return { hi: (z.hi ^ shifted.hi) >>> 0, lo: (z.lo ^ shifted.lo) >>> 0 };
  }

  /** Uniform 0…1, using the top 53 bits — the most a double can carry exactly. */
  unit(): number {
    const value = this.next();
    return (value.hi * 2097152 + (value.lo >>> 11)) / 9007199254740992;
  }

  range(lower: number, upper: number): number {
    return lower + (upper - lower) * this.unit();
  }

  chance(probability: number): boolean {
    return this.unit() < probability;
  }
}

/** The 16 bytes behind a UUID string. Non-hex characters make it a zero seed, not a crash. */
function uuidBytes(uuid: UUID): Uint8Array {
  const bytes = new Uint8Array(16);
  let index = 0;
  for (let position = 0; position + 1 < uuid.length && index < 16; position++) {
    const high = hexValue(uuid.charCodeAt(position));
    if (high < 0) continue;
    const low = hexValue(uuid.charCodeAt(position + 1));
    if (low < 0) continue;
    bytes[index++] = (high << 4) | low;
    position++;
  }
  return bytes;
}

function hexValue(code: number): number {
  if (code >= 48 && code <= 57) return code - 48;
  if (code >= 97 && code <= 102) return code - 87;
  if (code >= 65 && code <= 70) return code - 55;
  return -1;
}

/** Little-endian 64-bit load, matching how Swift reads the same bytes on ARM. */
function loadU64(bytes: Uint8Array, offset: number): { hi: number; lo: number } {
  const lo =
    ((bytes[offset + 3]! << 24) | (bytes[offset + 2]! << 16) | (bytes[offset + 1]! << 8) | bytes[offset]!) >>> 0;
  const hi =
    ((bytes[offset + 7]! << 24) | (bytes[offset + 6]! << 16) | (bytes[offset + 5]! << 8) | bytes[offset + 4]!) >>> 0;
  return { hi, lo };
}

function rawSeed(uuid: UUID): { hi: number; lo: number } {
  const bytes = uuidBytes(uuid);
  const high = loadU64(bytes, 0);
  const low = loadU64(bytes, 8);
  const mixed = mul64(low.hi, low.lo, 0xbf58476d, 0x1ce4e5b9);
  return { hi: (high.hi ^ mixed.hi) >>> 0, lo: (high.lo ^ mixed.lo) >>> 0 };
}

/**
 * The offline stream for one track of one pattern: independent per track, so muting a
 * track never reshuffles another one's probability rolls and a stem stays identical to its
 * share of the full mix.
 */
export function trackRandom(patternID: UUID, trackID: UUID): SeededRandom {
  const pattern = rawSeed(patternID);
  const track = rawSeed(trackID);
  const scaled = mul64(pattern.hi, pattern.lo, SPLITMIX_GAMMA_HI, SPLITMIX_GAMMA_LO);
  return new SeededRandom((scaled.hi ^ track.hi) >>> 0, (scaled.lo ^ track.lo) >>> 0);
}

/**
 * The live stream for one step. FNV-1a over the pattern's bytes plus the pass, track and
 * step — deliberately not a hash of the UUID string, which would be salted per process: a
 * probability pattern has to sound the same tomorrow as it does now while still differing
 * from pass to pass.
 */
export function stepRandom(patternID: UUID, pass: number, track: number, step: number): SeededRandom {
  const prime = { hi: 0x00000100, lo: 0x000001b3 };
  let hash = { hi: 0xcbf29ce4, lo: 0x84222325 };
  const bytes = uuidBytes(patternID);
  for (let index = 0; index < 16; index++) {
    hash = mul64(hash.hi, (hash.lo ^ bytes[index]!) >>> 0, prime.hi, prime.lo);
  }
  for (const value of [pass, track, step]) {
    const signed = value | 0;
    const hi = signed < 0 ? 0xffffffff : 0;
    hash = mul64((hash.hi ^ hi) >>> 0, (hash.lo ^ signed) >>> 0, prime.hi, prime.lo);
  }
  return new SeededRandom(hash.hi, hash.lo);
}

// MARK: - Biquad

export type BiquadKind =
  | 'lowPass'
  | 'highPass'
  | 'bandPass'
  | 'notch'
  | 'peaking'
  | 'lowShelf'
  | 'highShelf'
  | 'allPass';

/** Direct-form-II transposed biquad, Audio EQ Cookbook coefficients. */
export class Biquad {
  private b0 = 1;
  private b1 = 0;
  private b2 = 0;
  private a1 = 0;
  private a2 = 0;
  private z1 = 0;
  private z2 = 0;

  set(kind: BiquadKind, frequency: number, q: number, sampleRate: number, gainDB = 0): this {
    const nyquist = sampleRate * 0.5;
    const f = clamp(frequency, 10, nyquist * 0.98);
    const safeQ = Math.max(q, 0.05);
    const omega = (2 * Math.PI * f) / sampleRate;
    const sinOmega = Math.sin(omega);
    const cosOmega = Math.cos(omega);
    const alpha = sinOmega / (2 * safeQ);
    const a = Math.pow(10, gainDB / 40);

    let nb0 = 1;
    let nb1 = 0;
    let nb2 = 0;
    let na0 = 1;
    let na1 = 0;
    let na2 = 0;

    switch (kind) {
      case 'lowPass':
        nb0 = (1 - cosOmega) / 2;
        nb1 = 1 - cosOmega;
        nb2 = nb0;
        na0 = 1 + alpha;
        na1 = -2 * cosOmega;
        na2 = 1 - alpha;
        break;
      case 'highPass':
        nb0 = (1 + cosOmega) / 2;
        nb1 = -(1 + cosOmega);
        nb2 = nb0;
        na0 = 1 + alpha;
        na1 = -2 * cosOmega;
        na2 = 1 - alpha;
        break;
      case 'bandPass':
        nb0 = alpha;
        nb1 = 0;
        nb2 = -alpha;
        na0 = 1 + alpha;
        na1 = -2 * cosOmega;
        na2 = 1 - alpha;
        break;
      case 'notch':
        nb0 = 1;
        nb1 = -2 * cosOmega;
        nb2 = 1;
        na0 = 1 + alpha;
        na1 = -2 * cosOmega;
        na2 = 1 - alpha;
        break;
      case 'peaking':
        nb0 = 1 + alpha * a;
        nb1 = -2 * cosOmega;
        nb2 = 1 - alpha * a;
        na0 = 1 + alpha / a;
        na1 = -2 * cosOmega;
        na2 = 1 - alpha / a;
        break;
      case 'lowShelf': {
        const sqrtA = Math.sqrt(a);
        const twoSqrtAAlpha = 2 * sqrtA * alpha;
        nb0 = a * (a + 1 - (a - 1) * cosOmega + twoSqrtAAlpha);
        nb1 = 2 * a * (a - 1 - (a + 1) * cosOmega);
        nb2 = a * (a + 1 - (a - 1) * cosOmega - twoSqrtAAlpha);
        na0 = a + 1 + (a - 1) * cosOmega + twoSqrtAAlpha;
        na1 = -2 * (a - 1 + (a + 1) * cosOmega);
        na2 = a + 1 + (a - 1) * cosOmega - twoSqrtAAlpha;
        break;
      }
      case 'highShelf': {
        const sqrtA = Math.sqrt(a);
        const twoSqrtAAlpha = 2 * sqrtA * alpha;
        nb0 = a * (a + 1 + (a - 1) * cosOmega + twoSqrtAAlpha);
        nb1 = -2 * a * (a - 1 + (a + 1) * cosOmega);
        nb2 = a * (a + 1 + (a - 1) * cosOmega - twoSqrtAAlpha);
        na0 = a + 1 - (a - 1) * cosOmega + twoSqrtAAlpha;
        na1 = 2 * (a - 1 - (a + 1) * cosOmega);
        na2 = a + 1 - (a - 1) * cosOmega - twoSqrtAAlpha;
        break;
      }
      case 'allPass':
        nb0 = 1 - alpha;
        nb1 = -2 * cosOmega;
        nb2 = 1 + alpha;
        na0 = 1 + alpha;
        na1 = -2 * cosOmega;
        na2 = 1 - alpha;
        break;
    }

    this.b0 = nb0 / na0;
    this.b1 = nb1 / na0;
    this.b2 = nb2 / na0;
    this.a1 = na1 / na0;
    this.a2 = na2 / na0;
    return this;
  }

  reset(): void {
    this.z1 = 0;
    this.z2 = 0;
  }

  process(input: number): number {
    const output = this.b0 * input + this.z1;
    this.z1 = this.b1 * input - this.a1 * output + this.z2;
    this.z2 = this.b2 * input - this.a2 * output;
    return output;
  }
}

export class BiquadCascade {
  constructor(readonly stages: Biquad[]) {}

  process(input: number): number {
    let value = input;
    for (const stage of this.stages) value = stage.process(value);
    return value;
  }

  reset(): void {
    for (const stage of this.stages) stage.reset();
  }
}

/**
 * Sixth-order Butterworth. One biquad at 0.45 × Nyquist is only ~1.5 dB down at the fold
 * point, which is not enough to keep a downward sample-rate conversion clean.
 */
function antiAliasCascade(cutoff: number, sampleRate: number): BiquadCascade {
  return new BiquadCascade(
    [0.5176, 0.7071, 1.9319].map((q) => new Biquad().set('lowPass', cutoff, q, sampleRate)),
  );
}

/** One-pole smoothing coefficient for a cutoff in hertz. */
function onePoleCoefficient(cutoff: number, sampleRate: number): number {
  const f = clamp(cutoff, 20, sampleRate * 0.45);
  return clamp(1 - Math.exp((-2 * Math.PI * f) / sampleRate), 0.0001, 1);
}

class EnvelopeFollower {
  private attackCoefficient: number;
  private releaseCoefficient: number;
  private envelope = 0;

  constructor(attack: number, release: number, sampleRate: number) {
    this.attackCoefficient = Math.exp(-1 / Math.max(1, attack * sampleRate));
    this.releaseCoefficient = Math.exp(-1 / Math.max(1, release * sampleRate));
  }

  process(input: number): number {
    const coefficient = input > this.envelope ? this.attackCoefficient : this.releaseCoefficient;
    this.envelope = input + (this.envelope - input) * coefficient;
    return this.envelope;
  }
}

/** Fractional delay line with linear interpolation, used by every time-based effect. */
class DelayLine {
  private readonly buffer: Float32Array;
  private writeIndex = 0;

  constructor(maxDelaySamples: number) {
    this.buffer = new Float32Array(Math.max(4, Math.ceil(maxDelaySamples)));
  }

  write(value: number): void {
    this.buffer[this.writeIndex] = value;
    this.writeIndex = (this.writeIndex + 1) % this.buffer.length;
  }

  read(delaySamples: number): number {
    const length = this.buffer.length;
    const delay = clamp(delaySamples, 1, length - 2);
    const position = this.writeIndex - delay + length;
    const base = Math.floor(position);
    const fraction = position - base;
    const first = this.buffer[base % length]!;
    const second = this.buffer[(base + 1) % length]!;
    return first + (second - first) * fraction;
  }
}

// MARK: - Buffer helpers

export function frameCount(channels: readonly Float32Array[]): number {
  if (channels.length === 0) return 0;
  let frames = Number.MAX_SAFE_INTEGER;
  for (const channel of channels) frames = Math.min(frames, channel.length);
  return frames;
}

export function peakOf(channels: readonly Float32Array[]): number {
  let peak = 0;
  for (const channel of channels) {
    for (let index = 0; index < channel.length; index++) {
      const value = Math.abs(channel[index]!);
      if (value > peak) peak = value;
    }
  }
  return peak;
}

export function rmsOf(channel: Float32Array): number {
  if (channel.length === 0) return 0;
  let sum = 0;
  for (let index = 0; index < channel.length; index++) sum += channel[index]! * channel[index]!;
  return Math.sqrt(sum / channel.length);
}

export function scaleChannels(channels: Float32Array[], gain: number): void {
  if (gain === 1) return;
  for (const channel of channels) {
    for (let index = 0; index < channel.length; index++) channel[index]! *= gain;
  }
}

export function copyChannels(channels: readonly Float32Array[]): Float32Array[] {
  return channels.map((channel) => channel.slice());
}

/** Catmull-Rom interpolation, clamped at both ends. */
export function interpolateCatmullRom(samples: Float32Array, position: number): number {
  const length = samples.length;
  if (length === 0) return 0;
  if (!Number.isFinite(position)) return 0;
  const index = Math.floor(position);
  const t = position - index;
  const at = (i: number): number => samples[clamp(i, 0, length - 1)]!;
  const p0 = at(index - 1);
  const p1 = at(index);
  const p2 = at(index + 1);
  const p3 = at(index + 2);
  const a = 2 * p1;
  const b = p2 - p0;
  const c = 2 * p0 - 5 * p1 + 4 * p2 - p3;
  const d = -p0 + 3 * p1 - 3 * p2 + p3;
  return 0.5 * (a + b * t + c * t * t + d * t * t * t);
}

export function trimChannels(channels: readonly Float32Array[], startFrame: number, endFrame: number): Float32Array[] {
  const frames = frameCount(channels);
  const start = clamp(Math.round(startFrame), 0, frames);
  const end = clamp(Math.round(endFrame), start, frames);
  return channels.map((channel) => channel.slice(start, end));
}

export function reverseChannels(channels: Float32Array[]): void {
  for (const channel of channels) channel.reverse();
}

export function applyGain(channels: Float32Array[], db: number): void {
  if (Math.abs(db) < 1e-4) return;
  scaleChannels(channels, dbToLinear(db));
}

export function normalizeChannels(channels: Float32Array[], targetDB: number): void {
  const peak = peakOf(channels);
  // Below this the buffer is silence or dither, and scaling it would only amplify noise.
  if (peak <= 1e-6) return;
  scaleChannels(channels, dbToLinear(targetDB) / peak);
}

/** Fade table matching `fadeCurveGain` exactly, so a preview and a render agree. */
function fadeTable(count: number, curve: FadeCurve): Float32Array {
  const table = new Float32Array(count);
  for (let index = 0; index < count; index++) {
    table[index] = fadeCurveGain(curve, count > 1 ? index / (count - 1) : 1);
  }
  return table;
}

export function applyFadeIn(
  channels: Float32Array[],
  seconds: number,
  sampleRate: number,
  curve: FadeCurve,
): void {
  const frames = frameCount(channels);
  const count = Math.min(Math.round(seconds * sampleRate), frames);
  if (count <= 1) return;
  const table = fadeTable(count, curve);
  for (const channel of channels) {
    for (let index = 0; index < count; index++) channel[index]! *= table[index]!;
  }
}

export function applyFadeOut(
  channels: Float32Array[],
  seconds: number,
  sampleRate: number,
  curve: FadeCurve,
): void {
  const frames = frameCount(channels);
  const count = Math.min(Math.round(seconds * sampleRate), frames);
  if (count <= 1) return;
  const table = fadeTable(count, curve);
  for (const channel of channels) {
    for (let index = 0; index < count; index++) {
      channel[frames - count + index]! *= table[count - 1 - index]!;
    }
  }
}

/**
 * Silences a region, easing in and out *inside* it so the audio either side is untouched
 * and the edit cannot click.
 */
export function silenceRegion(
  channels: Float32Array[],
  fromFrame: number,
  toFrame: number,
  fadeSamples: number,
): void {
  const frames = frameCount(channels);
  const lower = clamp(Math.round(fromFrame), 0, frames);
  const upper = clamp(Math.round(toFrame), lower, frames);
  const length = upper - lower;
  if (length <= 0) return;
  const fade = clamp(fadeSamples, 0, Math.floor(length / 2));

  for (const channel of channels) {
    for (let index = 0; index < fade; index++) {
      const position = index / fade;
      channel[lower + index]! *= Math.cos((position * Math.PI) / 2);
      channel[upper - 1 - index]! *= Math.cos((position * Math.PI) / 2);
    }
    channel.fill(0, lower + fade, upper - fade);
  }
}

/**
 * Joins finished renders end to end, ramping either side of every seam.
 *
 * This is what makes "delete a region" mean delete rather than mute: the surviving pieces
 * are rendered separately and butted together here. The pieces do not overlap, so the seam
 * is a sine fade out of one and into the next rather than a crossfade — four milliseconds,
 * short enough that a join in the middle of a sustained note is inaudible and long enough
 * that a join between two transients cannot step.
 *
 * The result takes the widest channel count in the set; a mono piece joined to a stereo one
 * is copied across rather than being allowed to drop a side halfway through the file.
 */
export function concatenateRenders(parts: readonly RenderedAudio[], seamSeconds: number): RenderedAudio {
  const usable = parts.filter((part) => frameCount(part.channels) > 0);
  const first = usable[0];
  if (first === undefined) return { channels: [new Float32Array(1)], sampleRate: parts[0]?.sampleRate ?? 48000 };
  if (usable.length === 1) return first;

  const sampleRate = first.sampleRate;
  const channelCount = usable.reduce((widest, part) => Math.max(widest, part.channels.length), 1);
  const total = usable.reduce((sum, part) => sum + frameCount(part.channels), 0);
  const channels: Float32Array[] = [];
  for (let index = 0; index < channelCount; index++) channels.push(new Float32Array(total));

  const seam = Math.max(1, Math.round(Math.max(0, seamSeconds) * sampleRate));
  let offset = 0;
  for (let partIndex = 0; partIndex < usable.length; partIndex++) {
    const part = usable[partIndex]!;
    const frames = frameCount(part.channels);
    const ramp = Math.min(seam, Math.floor(frames / 2));
    for (let channel = 0; channel < channelCount; channel++) {
      const source = part.channels[Math.min(channel, part.channels.length - 1)]!;
      const target = channels[channel]!;
      target.set(source.subarray(0, frames), offset);
      // Only the edges that touch another piece are ramped: the very start and the very end
      // of the finished file keep whatever fades the edit itself asked for.
      if (partIndex > 0) {
        for (let index = 0; index < ramp; index++) {
          target[offset + index]! *= Math.sin((index / ramp) * (Math.PI / 2));
        }
      }
      if (partIndex < usable.length - 1) {
        for (let index = 0; index < ramp; index++) {
          target[offset + frames - 1 - index]! *= Math.sin((index / ramp) * (Math.PI / 2));
        }
      }
    }
    offset += frames;
  }
  return { channels, sampleRate };
}

/** ADSR re-shaping of a finished clip. Segments are squeezed if they exceed the length. */
export function applyAmpEnvelope(
  channels: Float32Array[],
  envelope: AmpEnvelope,
  sampleRate: number,
): void {
  const frames = frameCount(channels);
  if (frames <= 1) return;

  const sustain = clamp(envelope.sustain, 0, 1);
  let attack = Math.round(Math.max(envelope.attack, 0) * sampleRate);
  let decay = Math.round(Math.max(envelope.decay, 0) * sampleRate);
  let release = Math.round(Math.max(envelope.release, 0) * sampleRate);

  const requested = attack + decay + release;
  if (requested > frames && requested > 0) {
    const ratio = frames / requested;
    attack = Math.floor(attack * ratio);
    decay = Math.floor(decay * ratio);
    release = Math.max(0, frames - attack - decay);
  }

  const curve = new Float32Array(frames);
  curve.fill(sustain);
  for (let index = 0; index < attack; index++) curve[index] = index / Math.max(1, attack);
  for (let index = 0; index < decay; index++) {
    const position = index / Math.max(1, decay);
    curve[attack + index] = 1 + (sustain - 1) * position;
  }
  const releaseStart = frames - release;
  for (let index = 0; index < release; index++) {
    curve[releaseStart + index] = sustain * (1 - index / Math.max(1, release));
  }

  for (const channel of channels) {
    for (let index = 0; index < frames; index++) channel[index]! *= curve[index]!;
  }
}

/**
 * Equal-power crossfade of the tail into the head, so a looping clip has no seam. A
 * correlated loop point then does not bulge in the middle of the blend, and an
 * uncorrelated one does not dip.
 */
export function crossfadeLoop(channels: Float32Array[], crossfadeSamples: number): void {
  const frames = frameCount(channels);
  const count = clamp(Math.round(crossfadeSamples), 0, Math.floor(frames / 2));
  if (count <= 1) return;

  for (const channel of channels) {
    const head = channel.slice(0, count);
    for (let index = 0; index < count; index++) {
      const position = index / (count - 1);
      const fadeOut = Math.cos((position * Math.PI) / 2);
      const fadeIn = Math.sin((position * Math.PI) / 2);
      const tailIndex = frames - count + index;
      channel[tailIndex] = channel[tailIndex]! * fadeOut + head[index]! * fadeIn;
    }
  }
}

export function makeStereo(channels: readonly Float32Array[]): Float32Array[] {
  if (channels.length >= 2) return [channels[0]!, channels[1]!];
  if (channels.length === 1) return [channels[0]!, channels[0]!.slice()];
  return [new Float32Array(1), new Float32Array(1)];
}

// MARK: - Rate, stretch, pitch

/**
 * Sample-rate conversion with Catmull-Rom interpolation. Downward conversions are
 * low-passed first: without it, content above the new Nyquist folds back as inharmonic
 * whistling that no later processing can remove.
 */
export async function resample(
  channels: readonly Float32Array[],
  from: number,
  to: number,
  budget?: RenderBudget,
): Promise<Float32Array[]> {
  if (channels.length === 0 || !(from > 0) || !(to > 0)) return copyChannels(channels);
  if (Math.abs(from - to) <= 1e-6) return copyChannels(channels);

  const ratio = from / to;
  const needsAntiAliasing = to < from;
  const cutoff = 0.45 * Math.min(from, to);
  const result: Float32Array[] = [];

  for (const channel of channels) {
    if (channel.length <= 1) {
      result.push(channel.slice());
      continue;
    }
    let source = channel;
    if (needsAntiAliasing) {
      source = channel.slice();
      const cascade = antiAliasCascade(cutoff, from);
      await forEachBlock(source.length, BLOCK, budget, (start, end) => {
        for (let index = start; index < end; index++) source[index] = cascade.process(source[index]!);
      });
    }
    const outputCount = Math.max(1, Math.round(channel.length / ratio));
    const output = new Float32Array(outputCount);
    const reader = source;
    await forEachBlock(outputCount, BLOCK, budget, (start, end) => {
      for (let index = start; index < end; index++) {
        output[index] = interpolateCatmullRom(reader, index * ratio);
      }
    });
    result.push(output);
  }
  return result;
}

/** 2048 frames is ~46 ms at 44.1 kHz: long enough for a low bass period, short enough
 *  that a transient is not smeared across grains. */
function stretchWindowSize(sampleRate: number): number {
  if (sampleRate < 32000) return 1024;
  if (sampleRate > 70000) return 4096;
  return 2048;
}

const GRAIN_HANN = 0;
const GRAIN_FLAT_START = 1;
const GRAIN_FLAT_END = 2;
const GRAIN_FLAT = 3;

/**
 * Four grain windows in one table. The first grain's rise and the last grain's fall are
 * flattened so the overlap sum stays at full level at both ends of the output — a
 * stretched drum hit then still starts on its transient instead of fading in.
 */
function makeGrainWindows(window: number): Float32Array {
  const table = new Float32Array(window * 4);
  const half = window >> 1;
  for (let index = 0; index < window; index++) {
    // Periodic Hann: at a hop of window/4 these sum to exactly 2.0.
    const hann = 0.5 * (1 - Math.cos((2 * Math.PI * index) / window));
    table[GRAIN_HANN * window + index] = hann;
    table[GRAIN_FLAT_START * window + index] = index < half ? 1 : hann;
    table[GRAIN_FLAT_END * window + index] = index < half ? hann : 1;
    table[GRAIN_FLAT * window + index] = 1;
  }
  return table;
}

function monoReference(channels: readonly Float32Array[], frames: number): Float32Array {
  if (channels.length === 1) return channels[0]!;
  const reference = new Float32Array(frames);
  const scale = 1 / channels.length;
  for (const channel of channels) {
    for (let index = 0; index < frames; index++) reference[index]! += channel[index]! * scale;
  }
  return reference;
}

/** Decimation factor for the WSOLA similarity search. See `timeStretch`. */
const SEARCH_DECIMATION = 8;

/**
 * WSOLA time stretch: changes duration without changing pitch.
 *
 * `rate` is a speed multiplier — 2 is twice as fast and half as long. Grains leave the
 * input at `hop × rate` and land in the output at a fixed `hop`; the normalised
 * cross-correlation search is what keeps successive grains phase-aligned, and is the whole
 * difference between WSOLA and the metallic flanging of naive overlap-add.
 *
 * The search runs in two stages because JavaScript has no vDSP: a coarse pass over an
 * 8× decimated copy finds the neighbourhood for an eighth of the arithmetic, then a
 * full-rate pass over ±8 samples finds the exact lag. The answer is the same lag a direct
 * search would return whenever the correlation surface is smooth at that scale, which for
 * a signal band-limited by the decimation filter it is.
 */
export async function timeStretch(
  channels: readonly Float32Array[],
  rate: number,
  sampleRate: number,
  budget?: RenderBudget,
): Promise<Float32Array[]> {
  if (channels.length === 0) return copyChannels(channels);
  const inputCount = frameCount(channels);
  if (inputCount === 0) return copyChannels(channels);

  const safeRate = Number.isFinite(rate) ? clamp(rate, 0.1, 10) : 1;
  if (Math.abs(safeRate - 1) <= 1e-6) return copyChannels(channels);

  const outputCount = Math.max(1, Math.round(inputCount / safeRate));

  // Short material cannot host a full-size grain, so the window shrinks rather than the
  // algorithm bailing out to a pitch-shifting resample.
  let window = stretchWindowSize(sampleRate);
  while (window > 128 && inputCount < window * 2) window = window >> 1;
  if (inputCount < window * 2) {
    return channels.map((channel) => {
      const output = new Float32Array(outputCount);
      output.set(channel.subarray(0, Math.min(channel.length, outputCount)));
      return output;
    });
  }

  const synthesisHop = window >> 2;
  const searchRadius = window >> 2;
  const correlationLength = window >> 1;
  const padded = outputCount + window;
  const windows = makeGrainWindows(window);
  const reference = monoReference(channels, inputCount);

  // Decimated copy for the coarse search. A boxcar of the decimation length is a crude but
  // adequate anti-alias filter for a similarity measure that only needs the envelope.
  const decimatedLength = Math.ceil(inputCount / SEARCH_DECIMATION);
  const decimated = new Float32Array(decimatedLength);
  for (let index = 0; index < decimatedLength; index++) {
    let sum = 0;
    const start = index * SEARCH_DECIMATION;
    const end = Math.min(inputCount, start + SEARCH_DECIMATION);
    for (let inner = start; inner < end; inner++) sum += reference[inner]!;
    decimated[index] = sum / SEARCH_DECIMATION;
  }

  const template = new Float32Array(correlationLength);
  const decimatedTemplate = new Float32Array(Math.ceil(correlationLength / SEARCH_DECIMATION));

  function sampleAt(data: Float32Array, index: number): number {
    return index >= 0 && index < data.length ? data[index]! : 0;
  }

  function correlate(data: Float32Array, pattern: Float32Array, origin: number): number {
    let dot = 0;
    let energy = 0;
    for (let index = 0; index < pattern.length; index++) {
      const value = sampleAt(data, origin + index);
      dot += value * pattern[index]!;
      energy += value * value;
    }
    return energy > 1e-12 ? dot / Math.sqrt(energy) : 0;
  }

  function locate(ideal: number): number {
    let bestScore = 0;
    let bestCoarse = ideal;
    const coarseIdeal = Math.round(ideal / SEARCH_DECIMATION);
    const coarseRadius = Math.ceil(searchRadius / SEARCH_DECIMATION);
    for (let offset = -coarseRadius; offset <= coarseRadius; offset++) {
      const score = correlate(decimated, decimatedTemplate, coarseIdeal + offset);
      if (score > bestScore) {
        bestScore = score;
        bestCoarse = (coarseIdeal + offset) * SEARCH_DECIMATION;
      }
    }
    // Nothing in the window resembles the template — silence, or noise with no
    // periodicity. Shifting the grain would only smear it, so take the ideal position.
    if (bestScore <= 0) return clamp(ideal, 0, inputCount - 1);

    let bestFine = bestCoarse;
    let fineScore = 0;
    for (let offset = -SEARCH_DECIMATION; offset <= SEARCH_DECIMATION; offset++) {
      const score = correlate(reference, template, bestCoarse + offset);
      if (score > fineScore) {
        fineScore = score;
        bestFine = bestCoarse + offset;
      }
    }
    return clamp(bestFine, 0, inputCount - 1);
  }

  interface Grain {
    analysis: number;
    synthesis: number;
    window: number;
  }

  const grains: Grain[] = [];
  const norm = new Float32Array(padded);
  const analysisHop = synthesisHop * safeRate;
  let synthesisPosition = 0;
  let idealAnalysis = 0;
  let isFirst = true;

  while (synthesisPosition < outputCount) {
    const analysis = isFirst ? 0 : locate(Math.round(idealAnalysis));
    const isLast = synthesisPosition + synthesisHop >= outputCount;
    const kind = isFirst
      ? isLast
        ? GRAIN_FLAT
        : GRAIN_FLAT_START
      : isLast
        ? GRAIN_FLAT_END
        : GRAIN_HANN;
    grains.push({ analysis, synthesis: synthesisPosition, window: kind });

    const available = Math.min(window, inputCount - analysis);
    for (let index = 0; index < available; index++) {
      norm[synthesisPosition + index]! += windows[kind * window + index]!;
    }

    // The next grain has to continue what this one started, so the match template is the
    // audio that naturally followed it one synthesis hop later.
    for (let index = 0; index < correlationLength; index++) {
      template[index] = sampleAt(reference, analysis + synthesisHop + index);
    }
    for (let index = 0; index < decimatedTemplate.length; index++) {
      let sum = 0;
      for (let inner = 0; inner < SEARCH_DECIMATION; inner++) {
        sum += sampleAt(template, index * SEARCH_DECIMATION + inner);
      }
      decimatedTemplate[index] = sum / SEARCH_DECIMATION;
    }

    synthesisPosition += synthesisHop;
    idealAnalysis += analysisHop;
    isFirst = false;
    if (budget !== undefined && grains.length % 64 === 0) await budget.tick();
  }

  // Dividing by the accumulated window sum rather than the theoretical 2.0 keeps the first
  // and last grains at unity.
  const gains = new Float32Array(outputCount);
  for (let index = 0; index < outputCount; index++) {
    const value = norm[index]!;
    gains[index] = value > 1e-4 ? 1 / value : 0;
  }

  const result: Float32Array[] = [];
  for (const channel of channels) {
    const output = new Float32Array(padded);
    for (const grain of grains) {
      const available = Math.min(window, inputCount - grain.analysis);
      const offset = grain.window * window;
      for (let index = 0; index < available; index++) {
        output[grain.synthesis + index]! += channel[grain.analysis + index]! * windows[offset + index]!;
      }
    }
    for (let index = 0; index < outputCount; index++) output[index]! *= gains[index]!;
    result.push(output.slice(0, outputCount));
    if (budget !== undefined) await budget.tick();
  }
  return result;
}

/**
 * Pitch shift that preserves duration: stretch by the pitch ratio, then resample by its
 * inverse. The stretch is what makes it a pitch shift rather than a tape-speed change, and
 * the resample puts the length back.
 */
export async function pitchShift(
  channels: readonly Float32Array[],
  cents: number,
  sampleRate: number,
  budget?: RenderBudget,
): Promise<Float32Array[]> {
  if (channels.length === 0 || !(sampleRate > 0) || !Number.isFinite(cents)) return copyChannels(channels);
  const safeCents = clamp(cents, -2500, 2500);
  if (Math.abs(safeCents) <= 0.01) return copyChannels(channels);

  const ratio = Math.pow(2, safeCents / 1200);
  const stretched = await timeStretch(channels, 1 / ratio, sampleRate, budget);
  return resample(stretched, sampleRate, sampleRate / ratio, budget);
}

// MARK: - FFT
//
// A radix-2 complex transform. The cepstral formant pass needs a full symmetric spectrum,
// an inverse transform of a real sequence, and a second forward transform of the result;
// a packed real transform hides the Nyquist bin and makes all three fiddly to get right,
// so this pays for the full complex transform. At 1024 points, offline, that is invisible.

class ComplexFFT {
  readonly size: number;
  private readonly cosTable: Float64Array;
  private readonly sinTable: Float64Array;
  private readonly reverse: Uint32Array;

  constructor(size: number) {
    this.size = size;
    this.cosTable = new Float64Array(size / 2);
    this.sinTable = new Float64Array(size / 2);
    for (let index = 0; index < size / 2; index++) {
      this.cosTable[index] = Math.cos((2 * Math.PI * index) / size);
      this.sinTable[index] = Math.sin((2 * Math.PI * index) / size);
    }
    this.reverse = new Uint32Array(size);
    const bits = Math.round(Math.log2(size));
    for (let index = 0; index < size; index++) {
      let value = 0;
      for (let bit = 0; bit < bits; bit++) {
        if ((index & (1 << bit)) !== 0) value |= 1 << (bits - 1 - bit);
      }
      this.reverse[index] = value;
    }
  }

  /** In-place transform. `inverse` includes the 1/N normalisation. */
  run(real: Float64Array, imaginary: Float64Array, inverse: boolean): void {
    const size = this.size;
    for (let index = 0; index < size; index++) {
      const target = this.reverse[index]!;
      if (target > index) {
        const tempReal = real[index]!;
        real[index] = real[target]!;
        real[target] = tempReal;
        const tempImaginary = imaginary[index]!;
        imaginary[index] = imaginary[target]!;
        imaginary[target] = tempImaginary;
      }
    }

    const sign = inverse ? 1 : -1;
    for (let span = 2; span <= size; span <<= 1) {
      const half = span >> 1;
      const step = size / span;
      for (let start = 0; start < size; start += span) {
        for (let index = 0; index < half; index++) {
          const twiddle = index * step;
          const cos = this.cosTable[twiddle]!;
          const sin = sign * this.sinTable[twiddle]!;
          const upper = start + index + half;
          const lower = start + index;
          const productReal = real[upper]! * cos - imaginary[upper]! * sin;
          const productImaginary = real[upper]! * sin + imaginary[upper]! * cos;
          real[upper] = real[lower]! - productReal;
          imaginary[upper] = imaginary[lower]! - productImaginary;
          real[lower] = real[lower]! + productReal;
          imaginary[lower] = imaginary[lower]! + productImaginary;
        }
      }
    }

    if (inverse) {
      const scale = 1 / size;
      for (let index = 0; index < size; index++) {
        real[index]! *= scale;
        imaginary[index]! *= scale;
      }
    }
  }
}

function hannWindow(size: number): Float64Array {
  const window = new Float64Array(size);
  for (let index = 0; index < size; index++) {
    window[index] = 0.5 * (1 - Math.cos((2 * Math.PI * index) / size));
  }
  return window;
}

// MARK: - Effects

/**
 * Seconds of decay a chain needs after its input stops. Deliberately generous: an export
 * that clips a reverb tail is a bug the user hears; one half a second too long is one
 * nobody notices.
 */
export function chainTailSeconds(chain: EffectChain): number {
  let tail = 0;
  for (const node of effectChainActiveNodes(chain)) {
    switch (node.kind) {
      case 'reverb':
        tail = Math.max(tail, 0.4 + effectNodeValue(node, 'size') * 4);
        break;
      case 'delay': {
        const time = effectNodeValue(node, 'time');
        const feedback = clamp(effectNodeValue(node, 'feedback'), 0, 0.95);
        // Repeats needed for the echo train to fall below -60 dB.
        const repeats = feedback > 0.001 ? Math.log(0.001) / Math.log(feedback) : 1;
        tail = Math.max(tail, Math.min(time * repeats, 12));
        break;
      }
      case 'chorus':
      case 'flanger':
        tail = Math.max(tail, 0.05);
        break;
      default:
        break;
    }
  }
  return tail;
}

export interface EffectContext {
  sampleRate: number;
  tempo: number;
  budget?: RenderBudget;
}

/** Applies every enabled node in order. Returns the channels; some effects change length. */
export async function applyEffectChain(
  channels: Float32Array[],
  chain: EffectChain,
  context: EffectContext,
): Promise<Float32Array[]> {
  let current = channels;
  for (const node of effectChainActiveNodes(chain)) {
    current = await applyEffectNode(current, node, context);
    if (context.budget !== undefined) await context.budget.tick();
  }
  return current;
}

export async function applyEffectNode(
  channels: Float32Array[],
  node: EffectNode,
  context: EffectContext,
): Promise<Float32Array[]> {
  const frames = frameCount(channels);
  if (frames === 0) return channels;
  const sampleRate = Math.max(context.sampleRate, 8000);
  const tempo = clamp(context.tempo, 20, 300);
  const budget = context.budget;
  const value = (key: string): number => effectNodeValue(node, key);

  switch (node.kind) {
    case 'eq':
      await applyEQ(channels, node, sampleRate, budget);
      return channels;
    case 'highPass':
    case 'lowPass':
      await applySlopeFilter(channels, node, node.kind === 'highPass' ? 'highPass' : 'lowPass', sampleRate, budget);
      return channels;
    case 'bandPass':
      await applyBandPass(channels, node, sampleRate, budget);
      return channels;
    case 'compressor':
      await compress(channels, {
        thresholdDB: value('threshold'),
        ratio: value('ratio'),
        attack: value('attack'),
        release: value('release'),
        makeupDB: value('makeup'),
        sampleRate,
        budget,
      });
      return channels;
    case 'limiter':
      await limit(channels, {
        ceilingDB: value('ceiling'),
        release: value('release'),
        driveDB: value('drive'),
        sampleRate,
        budget,
      });
      return channels;
    case 'noiseGate':
      await gate(channels, {
        thresholdDB: value('threshold'),
        attack: value('attack'),
        hold: value('hold'),
        release: value('release'),
        sampleRate,
        budget,
      });
      return channels;
    case 'distortion':
      await applyDistortion(channels, node, sampleRate, budget);
      return channels;
    case 'bitCrush':
      await applyBitCrush(channels, node, budget);
      return channels;
    case 'sampleRateReduce':
      await applySampleRateReduction(channels, node, sampleRate, budget);
      return channels;
    case 'ringMod':
      await applyRingMod(channels, value('frequency'), value('mix'), sampleRate, budget);
      return channels;
    case 'reverb':
      await applyReverb(channels, {
        size: value('size'),
        damping: value('damping'),
        preDelay: value('preDelay'),
        mix: value('mix'),
        sampleRate,
        budget,
      });
      return channels;
    case 'delay':
      await applyDelay(channels, {
        time: value('time'),
        feedback: value('feedback'),
        tone: value('tone'),
        sync: value('sync') >= 0.5,
        mix: value('mix'),
        tempo,
        sampleRate,
        budget,
      });
      return channels;
    case 'chorus':
      await applyChorus(channels, {
        rate: value('rate'),
        depth: value('depth'),
        voices: Math.round(value('voices')),
        mix: value('mix'),
        sampleRate,
        budget,
      });
      return channels;
    case 'flanger':
      await applyFlanger(channels, {
        rate: value('rate'),
        depth: value('depth'),
        feedback: value('feedback'),
        mix: value('mix'),
        sampleRate,
        budget,
      });
      return channels;
    case 'tremolo':
      await applyTremolo(channels, value('rate'), value('depth'), Math.round(value('shape')), sampleRate, budget);
      return channels;
    case 'pitchShift': {
      const cents = value('semitones') * 100 + value('cents');
      if (Math.abs(cents) <= 0.5) return channels;
      return pitchShift(channels, cents, sampleRate, budget);
    }
    case 'formantShift':
      await applyFormantShift(channels, value('shift'), sampleRate, budget);
      return channels;
    case 'timeStretch': {
      const speed = value('rate');
      if (Math.abs(speed - 1) <= 0.001) return channels;
      return timeStretch(channels, speed, sampleRate, budget);
    }
    case 'harmoniser':
      return applyHarmoniser(channels, {
        voice1: value('voice1'),
        voice2: value('voice2'),
        spread: value('spread'),
        mix: value('mix'),
        sampleRate,
        budget,
      });
    case 'autoTune':
      await applyAutoTune(channels, {
        strength: value('strength'),
        speed: value('speed'),
        root: Math.round(value('root')),
        scale: Math.round(value('scale')),
        sampleRate,
        budget,
      });
      return channels;
    case 'stutter':
      await applyStutter(channels, {
        division: Math.round(value('division')),
        gateLength: value('gateLength'),
        mix: value('mix'),
        tempo,
        sampleRate,
        budget,
      });
      return channels;
    case 'glitch':
      await applyGlitch(channels, {
        density: value('density'),
        sliceSize: value('sliceSize'),
        reverseChance: value('reverseChance'),
        pitchChance: value('pitchChance'),
        seed: Math.max(0, Math.round(value('seed'))),
        sampleRate,
        budget,
      });
      return channels;
    case 'gainTrim':
      applyGain(channels, value('gain'));
      return channels;
  }
}

async function applyEQ(
  channels: Float32Array[],
  node: EffectNode,
  sampleRate: number,
  budget: RenderBudget | undefined,
): Promise<void> {
  const lowGain = effectNodeValue(node, 'lowGain');
  const midGain = effectNodeValue(node, 'midGain');
  const highGain = effectNodeValue(node, 'highGain');
  if (Math.abs(lowGain) < 0.01 && Math.abs(midGain) < 0.01 && Math.abs(highGain) < 0.01) return;

  for (const channel of channels) {
    const cascade = new BiquadCascade([
      new Biquad().set('lowShelf', effectNodeValue(node, 'lowFreq'), 0.707, sampleRate, lowGain),
      new Biquad().set('peaking', effectNodeValue(node, 'midFreq'), effectNodeValue(node, 'midQ'), sampleRate, midGain),
      new Biquad().set('highShelf', effectNodeValue(node, 'highFreq'), 0.707, sampleRate, highGain),
    ]);
    await forEachBlock(channel.length, BLOCK, budget, (from, to) => {
      for (let index = from; index < to; index++) channel[index] = cascade.process(channel[index]!);
    });
  }
}

/**
 * 24 dB/octave high- or low-pass. The two sections use the Butterworth Q pair for a
 * fourth-order response rather than two identical 0.707 stages, which would sag 6 dB at
 * the corner instead of 3. Resonance is applied to the higher-Q section alone, so raising
 * it grows a defined peak instead of tilting the whole slope.
 */
async function applySlopeFilter(
  channels: Float32Array[],
  node: EffectNode,
  kind: 'highPass' | 'lowPass',
  sampleRate: number,
  budget: RenderBudget | undefined,
): Promise<void> {
  const frequency = effectNodeValue(node, 'frequency');
  const resonance = effectNodeValue(node, 'resonance');
  const nyquist = sampleRate * 0.5;
  if (kind === 'highPass' && frequency <= 20.5 && Math.abs(resonance) < 0.01) return;
  if (kind === 'lowPass' && frequency >= Math.min(20000, nyquist * 0.98) && Math.abs(resonance) < 0.01) return;

  const resonanceScale = Math.pow(10, resonance / 40);
  const firstQ = 0.5412;
  const secondQ = clamp(1.3066 * resonanceScale, 0.2, 20);

  for (const channel of channels) {
    const cascade = new BiquadCascade([
      new Biquad().set(kind, frequency, firstQ, sampleRate),
      new Biquad().set(kind, frequency, secondQ, sampleRate),
    ]);
    await forEachBlock(channel.length, BLOCK, budget, (from, to) => {
      for (let index = from; index < to; index++) channel[index] = cascade.process(channel[index]!);
    });
  }
}

/**
 * Two cascaded band-pass sections for 12 dB/octave skirts. Cascading narrows the composite
 * response, so each section is widened by the factor relating a two-section cascade to a
 * single one — without it the "Width" readout would lie by half an octave.
 */
async function applyBandPass(
  channels: Float32Array[],
  node: EffectNode,
  sampleRate: number,
  budget: RenderBudget | undefined,
): Promise<void> {
  const frequency = effectNodeValue(node, 'frequency');
  const bandwidth = Math.max(effectNodeValue(node, 'bandwidth'), 0.05);
  const span = Math.pow(2, bandwidth);
  const totalQ = Math.sqrt(span) / Math.max(span - 1, 1e-6);
  const stageQ = clamp(totalQ * 0.6436, 0.05, 40);

  for (const channel of channels) {
    const cascade = new BiquadCascade([
      new Biquad().set('bandPass', frequency, stageQ, sampleRate),
      new Biquad().set('bandPass', frequency, stageQ, sampleRate),
    ]);
    await forEachBlock(channel.length, BLOCK, budget, (from, to) => {
      for (let index = from; index < to; index++) channel[index] = cascade.process(channel[index]!);
    });
  }
}

/** Reflects off ±1 as a period-4 triangle, so an extreme drive costs what a mild one does. */
function foldback(value: number): number {
  let wrapped = (value + 1) % 4;
  if (wrapped < 0) wrapped += 4;
  return wrapped <= 2 ? wrapped - 1 : 3 - wrapped;
}

function distortionShape(input: number, drive: number, character: number): number {
  const driven = input * drive;
  switch (character) {
    case 1:
      return clamp(driven, -1, 1);
    case 2:
      return foldback(driven);
    case 3:
      // Valve asymmetry: the negative half compresses sooner and less far, which is what
      // puts even harmonics into the result.
      return driven >= 0 ? Math.tanh(driven) : Math.tanh(driven * 0.6) * 0.85;
    default:
      return Math.tanh(driven);
  }
}

async function applyDistortion(
  channels: Float32Array[],
  node: EffectNode,
  sampleRate: number,
  budget: RenderBudget | undefined,
): Promise<void> {
  const mix = effectNodeValue(node, 'mix');
  if (mix <= 0.0001) return;

  const drive = effectNodeValue(node, 'drive');
  const character = Math.round(effectNodeValue(node, 'character'));
  const preGain = dbToLinear(drive * 36);
  // The saturating curves fall short of full scale at low drive, so they are normalised by
  // their own response to a full-scale input: the drive knob then changes the sound rather
  // than the loudness. Clipping and foldback already span ±1 at any drive.
  const normalisation =
    character === 1 || character === 2
      ? 1
      : 1 / clamp(Math.abs(distortionShape(1, preGain, character)), 0.25, 1);

  const tilt = (effectNodeValue(node, 'tone') - 0.5) * 12;
  const stages: Array<() => Biquad> = [];
  if (Math.abs(tilt) > 0.01) {
    stages.push(() => new Biquad().set('lowShelf', 800, 0.707, sampleRate, -tilt));
    stages.push(() => new Biquad().set('highShelf', 800, 0.707, sampleRate, tilt));
  }
  if (character === 3) {
    // The asymmetric curve rectifies as much as it distorts; without this the clip comes
    // back with an offset that eats headroom and thumps on playback.
    stages.push(() => new Biquad().set('highPass', 20, 0.707, sampleRate));
  }

  for (const channel of channels) {
    const cascade = new BiquadCascade(stages.map((make) => make()));
    await forEachBlock(channel.length, BLOCK, budget, (from, to) => {
      for (let index = from; index < to; index++) {
        const dry = channel[index]!;
        const wet = cascade.process(distortionShape(dry, preGain, character) * normalisation);
        channel[index] = dry * (1 - mix) + wet * mix;
      }
    });
  }
}

async function applyBitCrush(
  channels: Float32Array[],
  node: EffectNode,
  budget: RenderBudget | undefined,
): Promise<void> {
  const mix = effectNodeValue(node, 'mix');
  const bits = effectNodeValue(node, 'bits');
  if (mix <= 0.0001 || bits >= 15.5) return;

  // Mid-rise quantisation: at one bit the signal collapses to ±half a step, which is a
  // real one-bit signal. Mid-tread would leave a third, silent level.
  const step = 2 / Math.pow(2, bits);
  for (const channel of channels) {
    await forEachBlock(channel.length, BLOCK, budget, (from, to) => {
      for (let index = from; index < to; index++) {
        const dry = channel[index]!;
        const quantised = clamp((Math.floor(dry / step) + 0.5) * step, -1, 1);
        channel[index] = dry * (1 - mix) + quantised * mix;
      }
    });
  }
}

async function applySampleRateReduction(
  channels: Float32Array[],
  node: EffectNode,
  sampleRate: number,
  budget: RenderBudget | undefined,
): Promise<void> {
  const mix = effectNodeValue(node, 'mix');
  const target = effectNodeValue(node, 'rate');
  if (mix <= 0.0001 || target >= sampleRate) return;

  const increment = clamp(target / sampleRate, 1e-5, 1);
  for (const channel of channels) {
    let phase = 1;
    let held = 0;
    await forEachBlock(channel.length, BLOCK, budget, (from, to) => {
      for (let index = from; index < to; index++) {
        const dry = channel[index]!;
        if (phase >= 1) {
          phase -= 1;
          held = dry;
        }
        channel[index] = dry * (1 - mix) + held * mix;
        phase += increment;
      }
    });
  }
}

/** The carrier is shared across channels so a stereo source keeps its image. */
async function applyRingMod(
  channels: Float32Array[],
  frequency: number,
  mix: number,
  sampleRate: number,
  budget: RenderBudget | undefined,
): Promise<void> {
  if (mix <= 0.0001) return;
  const frames = frameCount(channels);
  const increment = Math.max(frequency, 0) / sampleRate;
  let phase = 0;
  await forEachBlock(frames, BLOCK, budget, (from, to) => {
    for (let index = from; index < to; index++) {
      const carrier = Math.sin(2 * Math.PI * phase);
      for (const channel of channels) {
        const dry = channel[index]!;
        channel[index] = dry * (1 - mix) + dry * carrier * mix;
      }
      phase += increment;
      if (phase >= 1) phase -= 1;
    }
  });
}

// Freeverb: prime comb lengths from Jezar's original, measured at 44.1 kHz.
const COMB_TUNINGS = [1116, 1188, 1277, 1356, 1422, 1491, 1557, 1617];
const ALLPASS_TUNINGS = [556, 441, 341, 225];
const STEREO_SPREAD = 23;
const REVERB_REFERENCE_RATE = 44100;
/** Keeps eight resonant combs from summing into an instant overload. */
const REVERB_FIXED_GAIN = 0.015;

function scaledTuning(tuning: number, sampleRate: number): number {
  return Math.max(4, Math.round((tuning * sampleRate) / REVERB_REFERENCE_RATE));
}

/** Lowpass-feedback comb: the damping one-pole inside the loop is what darkens the tail. */
class ReverbComb {
  private readonly buffer: Float32Array;
  private index = 0;
  private store = 0;

  constructor(
    length: number,
    private readonly feedback: number,
    private readonly damping: number,
  ) {
    this.buffer = new Float32Array(length);
  }

  process(input: number): number {
    const output = this.buffer[this.index]!;
    this.store = output * (1 - this.damping) + this.store * this.damping;
    this.buffer[this.index] = input + this.store * this.feedback;
    this.index = (this.index + 1) % this.buffer.length;
    return output;
  }
}

class ReverbAllPass {
  private readonly buffer: Float32Array;
  private index = 0;

  constructor(length: number) {
    this.buffer = new Float32Array(length);
  }

  process(input: number): number {
    const buffered = this.buffer[this.index]!;
    const output = -input + buffered;
    this.buffer[this.index] = input + buffered * 0.5;
    this.index = (this.index + 1) % this.buffer.length;
    return output;
  }
}

class FreeverbChannel {
  private readonly combs: ReverbComb[];
  private readonly allPasses: ReverbAllPass[];

  constructor(sampleRate: number, spreadOffset: number, feedback: number, damping: number) {
    this.combs = COMB_TUNINGS.map(
      (tuning) => new ReverbComb(scaledTuning(tuning + spreadOffset, sampleRate), feedback, damping),
    );
    this.allPasses = ALLPASS_TUNINGS.map(
      (tuning) => new ReverbAllPass(scaledTuning(tuning + spreadOffset, sampleRate)),
    );
  }

  process(input: number): number {
    let accumulated = 0;
    for (const comb of this.combs) accumulated += comb.process(input);
    let value = accumulated;
    for (const allPass of this.allPasses) value = allPass.process(value);
    return value;
  }
}

async function applyReverb(
  channels: Float32Array[],
  options: {
    size: number;
    damping: number;
    preDelay: number;
    mix: number;
    sampleRate: number;
    budget: RenderBudget | undefined;
  },
): Promise<void> {
  if (options.mix <= 0.0001) return;
  const frames = frameCount(channels);
  const feedback = clamp(options.size, 0, 1) * 0.28 + 0.7;
  const damping = clamp(options.damping, 0, 1) * 0.4;
  const units = channels.map(
    (_channel, index) => new FreeverbChannel(options.sampleRate, index * STEREO_SPREAD, feedback, damping),
  );

  const preDelayFrames = Math.max(1, Math.round(clamp(options.preDelay, 0, 0.2) * options.sampleRate));
  const preDelayLine = new DelayLine(preDelayFrames + 4);
  const inputScale = 1 / channels.length;
  const wetLevel = options.mix;
  const dryLevel = 1 - options.mix;

  await forEachBlock(frames, BLOCK, options.budget, (from, to) => {
    for (let index = from; index < to; index++) {
      let send = 0;
      for (const channel of channels) send += channel[index]!;
      send *= inputScale * REVERB_FIXED_GAIN;
      preDelayLine.write(send);
      const delayed = preDelayLine.read(preDelayFrames);
      for (let channelIndex = 0; channelIndex < channels.length; channelIndex++) {
        const channel = channels[channelIndex]!;
        const wet = units[channelIndex]!.process(delayed);
        channel[index] = channel[index]! * dryLevel + wet * wetLevel;
      }
    }
  });
}

/** Musical divisions a tempo-synced delay can land on, in beats. */
const SYNC_DIVISIONS = [4, 3, 2, 1.5, 1, 2 / 3, 0.5, 1 / 3, 0.25, 1 / 6, 0.125];

/**
 * The nearest musical division to a requested delay time. The comparison is made on the
 * ratio rather than the difference because delay time is a logarithmic control: 40 ms is
 * as far from 20 ms as 800 ms is from 400 ms.
 */
function syncedDelaySeconds(time: number, tempo: number): number {
  const beatSeconds = 60 / clamp(tempo, 20, 300);
  const requested = Math.max(time, 0.005);
  let best = requested;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const beats of SYNC_DIVISIONS) {
    const candidate = beats * beatSeconds;
    if (candidate < 0.005 || candidate > 2) continue;
    const distance = Math.abs(Math.log(candidate / requested));
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }
  return best;
}

async function applyDelay(
  channels: Float32Array[],
  options: {
    time: number;
    feedback: number;
    tone: number;
    sync: boolean;
    mix: number;
    tempo: number;
    sampleRate: number;
    budget: RenderBudget | undefined;
  },
): Promise<void> {
  if (options.mix <= 0.0001) return;
  const frames = frameCount(channels);
  const seconds = options.sync ? syncedDelaySeconds(options.time, options.tempo) : options.time;
  const delaySamples = Math.max(1, Math.min(seconds, 2) * options.sampleRate);
  const feedbackGain = clamp(options.feedback, 0, 0.95);
  const toneCoefficient = onePoleCoefficient(options.tone, options.sampleRate);

  const lines = channels.map(() => new DelayLine(delaySamples + 8));
  const toneState = new Float64Array(channels.length);
  const wetLevel = options.mix;
  const dryLevel = 1 - options.mix;

  await forEachBlock(frames, BLOCK, options.budget, (from, to) => {
    for (let index = from; index < to; index++) {
      for (let channelIndex = 0; channelIndex < channels.length; channelIndex++) {
        const channel = channels[channelIndex]!;
        const dry = channel[index]!;
        const tap = lines[channelIndex]!.read(delaySamples);
        // A one-pole low-pass inside the loop makes each repeat darker than the one
        // before it, the way a tape echo is.
        toneState[channelIndex]! += toneCoefficient * (tap - toneState[channelIndex]!);
        const wet = toneState[channelIndex]!;
        lines[channelIndex]!.write(dry + wet * feedbackGain);
        channel[index] = dry * dryLevel + wet * wetLevel;
      }
    }
  });
}

async function applyChorus(
  channels: Float32Array[],
  options: {
    rate: number;
    depth: number;
    voices: number;
    mix: number;
    sampleRate: number;
    budget: RenderBudget | undefined;
  },
): Promise<void> {
  if (options.mix <= 0.0001) return;
  const frames = frameCount(channels);
  const voiceCount = clamp(Math.round(options.voices), 1, 4);
  const minimumDelay = 0.005 * options.sampleRate;
  const span = 0.02 * options.sampleRate;
  const swing = (clamp(options.depth, 0, 1) * span) / (2 * voiceCount);
  const phaseIncrement = Math.max(options.rate, 0) / options.sampleRate;
  const voiceScale = 1 / voiceCount;

  const lines = channels.map(() => new DelayLine(minimumDelay + span + 8));
  let phase = 0;
  const wetLevel = options.mix;
  const dryLevel = 1 - options.mix;

  await forEachBlock(frames, BLOCK, options.budget, (from, to) => {
    for (let index = from; index < to; index++) {
      for (let channelIndex = 0; channelIndex < channels.length; channelIndex++) {
        const channel = channels[channelIndex]!;
        const dry = channel[index]!;
        lines[channelIndex]!.write(dry);
        let wet = 0;
        for (let voice = 0; voice < voiceCount; voice++) {
          const voicePhase = phase + voice / voiceCount + channelIndex * 0.25;
          const base = minimumDelay + (span * (voice + 0.5)) / voiceCount;
          wet += lines[channelIndex]!.read(base + swing * Math.sin(2 * Math.PI * voicePhase));
        }
        channel[index] = dry * dryLevel + wet * voiceScale * wetLevel;
      }
      phase += phaseIncrement;
      if (phase >= 1) phase -= 1;
    }
  });
}

/**
 * Swept short delay with a feedback path that may be inverted. Negative feedback pulls the
 * comb's notches where positive feedback puts its peaks, which is the difference between a
 * hollow flange and a jet-engine one.
 */
async function applyFlanger(
  channels: Float32Array[],
  options: {
    rate: number;
    depth: number;
    feedback: number;
    mix: number;
    sampleRate: number;
    budget: RenderBudget | undefined;
  },
): Promise<void> {
  if (options.mix <= 0.0001) return;
  const frames = frameCount(channels);
  const minimumDelay = 0.0005 * options.sampleRate;
  const maximumDelay = 0.008 * options.sampleRate;
  const sweep = (maximumDelay - minimumDelay) * clamp(options.depth, 0, 1);
  const feedbackGain = clamp(options.feedback, -0.9, 0.9);
  const phaseIncrement = Math.max(options.rate, 0) / options.sampleRate;

  const lines = channels.map(() => new DelayLine(maximumDelay + 8));
  let phase = 0;
  const wetLevel = options.mix;
  const dryLevel = 1 - options.mix;

  await forEachBlock(frames, BLOCK, options.budget, (from, to) => {
    for (let index = from; index < to; index++) {
      for (let channelIndex = 0; channelIndex < channels.length; channelIndex++) {
        const channel = channels[channelIndex]!;
        const dry = channel[index]!;
        const channelPhase = phase + channelIndex * 0.25;
        const modulated = minimumDelay + sweep * (0.5 + 0.5 * Math.sin(2 * Math.PI * channelPhase));
        const wet = lines[channelIndex]!.read(modulated);
        lines[channelIndex]!.write(dry + wet * feedbackGain);
        channel[index] = dry * dryLevel + wet * wetLevel;
      }
      phase += phaseIncrement;
      if (phase >= 1) phase -= 1;
    }
  });
}

/**
 * Amplitude modulation. Shape 0 is a sine, 1 a triangle, 2 a hard square. The square is
 * band-unlimited on purpose — the edge is the sound of a chopper tremolo — but a 1 ms ramp
 * keeps it from being a full-scale discontinuity.
 */
async function applyTremolo(
  channels: Float32Array[],
  rate: number,
  depth: number,
  shape: number,
  sampleRate: number,
  budget: RenderBudget | undefined,
): Promise<void> {
  if (depth <= 0.0001) return;
  const frames = frameCount(channels);
  const phaseIncrement = Math.max(rate, 0) / sampleRate;
  const amount = clamp(depth, 0, 1);
  const edgeFrames = Math.max(1, 0.001 * sampleRate);
  let phase = 0;
  let squareLevel = 1;

  await forEachBlock(frames, BLOCK, budget, (from, to) => {
    for (let index = from; index < to; index++) {
      let shaped: number;
      if (shape === 1) {
        shaped = 1 - 2 * Math.abs(phase - 0.5);
      } else if (shape === 2) {
        const target = phase < 0.5 ? 1 : 0;
        squareLevel += clamp(target - squareLevel, -1 / edgeFrames, 1 / edgeFrames);
        shaped = squareLevel;
      } else {
        shaped = 0.5 + 0.5 * Math.sin(2 * Math.PI * phase);
      }
      const gain = 1 - amount * (1 - shaped);
      for (const channel of channels) channel[index]! *= gain;
      phase += phaseIncrement;
      if (phase >= 1) phase -= 1;
    }
  });
}

// MARK: - Dynamics

/**
 * Feed-forward compressor with a log-domain gain computer and a 6 dB soft knee. The
 * detector is linked across channels, so a hard-panned hit cannot drag the image sideways.
 */
async function compress(
  channels: Float32Array[],
  options: {
    thresholdDB: number;
    ratio: number;
    attack: number;
    release: number;
    makeupDB: number;
    sampleRate: number;
    budget: RenderBudget | undefined;
  },
): Promise<void> {
  const frames = frameCount(channels);
  const safeRatio = Math.max(options.ratio, 1);
  if (safeRatio <= 1.0001 && Math.abs(options.makeupDB) < 0.0001) return;

  const makeup = dbToLinear(options.makeupDB);
  const detector = new EnvelopeFollower(options.attack, options.release, options.sampleRate);
  const kneeWidth = 6;
  const halfKnee = kneeWidth / 2;

  await forEachBlock(frames, BLOCK, options.budget, (from, to) => {
    for (let index = from; index < to; index++) {
      let peak = 0;
      for (const channel of channels) peak = Math.max(peak, Math.abs(channel[index]!));
      const levelDB = linearToDB(detector.process(peak));
      const over = levelDB - options.thresholdDB;

      let compressedDB: number;
      if (over <= -halfKnee) {
        compressedDB = levelDB;
      } else if (over >= halfKnee) {
        compressedDB = options.thresholdDB + over / safeRatio;
      } else {
        // Quadratic interpolation across the knee: the curve and its slope are continuous
        // at both hinges, which is what stops a knee from ticking.
        const x = over + halfKnee;
        compressedDB = levelDB + ((1 / safeRatio - 1) * x * x) / (2 * kneeWidth);
      }
      const gain = dbToLinear(compressedDB - levelDB) * makeup;
      for (const channel of channels) channel[index]! *= gain;
    }
  });
}

/**
 * Brick-wall limiter with 5 ms of lookahead.
 *
 * Gain reduction is a sliding minimum of the required gain, release-slewed, then run
 * through a boxcar of the same length. That combination provably reaches its target before
 * the peak it is guarding against arrives, unlike a one-pole attack which only gets 63% of
 * the way there in one time constant. The matching delay keeps the output sample-aligned
 * with the input, which stems and multi-pad renders depend on.
 */
async function limit(
  channels: Float32Array[],
  options: {
    ceilingDB: number;
    release: number;
    driveDB: number;
    sampleRate: number;
    budget: RenderBudget | undefined;
  },
): Promise<void> {
  const frames = frameCount(channels);
  if (frames === 0) return;

  const lookahead = Math.max(2, Math.round(0.005 * Math.max(options.sampleRate, 8000)));
  const delayFrames = lookahead - 1;
  const ringLength = delayFrames + 1;
  const ceiling = dbToLinear(Math.min(options.ceilingDB, 0));
  const drive = dbToLinear(options.driveDB);
  const releaseCoefficient = Math.exp(-1 / (Math.max(options.release, 0.001) * options.sampleRate));

  // Monotonic deque holding the sliding minimum of the required gain.
  const windowValues = new Float64Array(lookahead);
  const windowIndices = new Int32Array(lookahead);
  let head = 0;
  let tail = 0;

  const meanBuffer = new Float64Array(lookahead).fill(1);
  let meanSum = lookahead;
  let meanIndex = 0;

  const delayLines = channels.map(() => new Float32Array(ringLength));
  let writeIndex = 0;
  let held = 1;

  await forEachBlock(frames + delayFrames, BLOCK, options.budget, (from, to) => {
    for (let step = from; step < to; step++) {
      const readIndex = (writeIndex + 1) % ringLength;
      let peak = 0;
      for (let channelIndex = 0; channelIndex < channels.length; channelIndex++) {
        const driven = step < frames ? channels[channelIndex]![step]! * drive : 0;
        delayLines[channelIndex]![writeIndex] = driven;
        peak = Math.max(peak, Math.abs(driven));
      }

      const required = peak > ceiling ? ceiling / peak : 1;
      while (tail > head && windowValues[(tail - 1) % lookahead]! >= required) tail--;
      windowValues[tail % lookahead] = required;
      windowIndices[tail % lookahead] = step;
      tail++;
      while (windowIndices[head % lookahead]! <= step - lookahead) head++;
      const minimum = windowValues[head % lookahead]!;

      held = minimum < held ? minimum : minimum + (held - minimum) * releaseCoefficient;

      meanSum -= meanBuffer[meanIndex]!;
      meanBuffer[meanIndex] = held;
      meanSum += held;
      meanIndex = (meanIndex + 1) % lookahead;
      const gain = meanSum / lookahead;

      if (step >= delayFrames) {
        const outputFrame = step - delayFrames;
        for (let channelIndex = 0; channelIndex < channels.length; channelIndex++) {
          channels[channelIndex]![outputFrame] = delayLines[channelIndex]![readIndex]! * gain;
        }
      }
      writeIndex += 1;
      if (writeIndex === ringLength) writeIndex = 0;
    }
  });
}

/**
 * Downward gate with 3 dB of hysteresis between its open and close thresholds, which is
 * what stops a sound hovering at the threshold from chattering.
 */
async function gate(
  channels: Float32Array[],
  options: {
    thresholdDB: number;
    attack: number;
    hold: number;
    release: number;
    sampleRate: number;
    budget: RenderBudget | undefined;
  },
): Promise<void> {
  const frames = frameCount(channels);
  if (frames === 0) return;

  // The detector is deliberately faster than the gate's own envelope: it decides *whether*
  // to open, while attack and release decide how the opening sounds.
  const detector = new EnvelopeFollower(0.0005, 0.01, options.sampleRate);
  const openDB = options.thresholdDB;
  const closeDB = options.thresholdDB - 3;
  const holdFrames = Math.round(Math.max(0, options.hold) * options.sampleRate);
  const attackStep = 1 / Math.max(1, options.attack * options.sampleRate);
  const releaseStep = 1 / Math.max(1, options.release * options.sampleRate);

  let isOpen = false;
  let holdCounter = 0;
  let gainValue = 0;

  await forEachBlock(frames, BLOCK, options.budget, (from, to) => {
    for (let index = from; index < to; index++) {
      let peak = 0;
      for (const channel of channels) peak = Math.max(peak, Math.abs(channel[index]!));
      const levelDB = linearToDB(detector.process(peak));

      if (levelDB >= openDB) {
        isOpen = true;
        holdCounter = holdFrames;
      } else if (levelDB < closeDB) {
        if (holdCounter > 0) holdCounter -= 1;
        else isOpen = false;
      }

      gainValue = isOpen ? Math.min(1, gainValue + attackStep) : Math.max(0, gainValue - releaseStep);
      for (const channel of channels) channel[index]! *= gainValue;
    }
  });
}

// MARK: - Voice and rhythmic effects

/**
 * Cepstral formant shift: the spectral envelope is moved without moving the pitch, which
 * is what separates "a giant" from "a slowed-down recording".
 *
 * Each frame is transformed, its log magnitude is transformed again to a cepstrum, the
 * high quefrencies (the pitch structure) are lifted out, and what remains is the envelope.
 * Warping that envelope and applying the ratio between old and new as a gain per bin
 * leaves every harmonic exactly where it was.
 */
async function applyFormantShift(
  channels: Float32Array[],
  semitones: number,
  sampleRate: number,
  budget: RenderBudget | undefined,
): Promise<void> {
  const frames = frameCount(channels);
  if (frames === 0 || Math.abs(semitones) <= 0.01) return;

  const size = 1024;
  if (frames <= size) return;

  const fft = new ComplexFFT(size);
  const hop = size / 4;
  const ratio = semitonesToRate(semitones);
  const window = hannWindow(size);
  const half = size / 2;
  // Quefrencies below ~2.5 ms are shorter than any voiced pitch period the app will meet
  // (that would be an F0 above 400 Hz), so the cut isolates the envelope.
  const lifterCutoff = clamp(Math.round(0.0025 * sampleRate), 8, half - 1);

  const real = new Float64Array(size);
  const imaginary = new Float64Array(size);
  const spectrumReal = new Float64Array(size);
  const spectrumImaginary = new Float64Array(size);
  const envelope = new Float64Array(half + 1);
  const gainCurve = new Float64Array(half + 1);

  for (let channelIndex = 0; channelIndex < channels.length; channelIndex++) {
    const source = channels[channelIndex]!;
    const leadIn = size;
    const paddedCount = leadIn + frames + 2 * size;
    const padded = new Float32Array(paddedCount);
    padded.set(source.subarray(0, frames), leadIn);
    const output = new Float64Array(paddedCount);
    const normalisation = new Float64Array(paddedCount);

    for (let position = 0; position + size <= paddedCount; position += hop) {
      for (let index = 0; index < size; index++) {
        real[index] = padded[position + index]! * window[index]!;
        imaginary[index] = 0;
      }
      fft.run(real, imaginary, false);

      for (let bin = 0; bin < size; bin++) {
        spectrumReal[bin] = real[bin]!;
        spectrumImaginary[bin] = imaginary[bin]!;
        const magnitude = Math.hypot(real[bin]!, imaginary[bin]!);
        real[bin] = Math.log(Math.max(magnitude, 1e-7));
        imaginary[bin] = 0;
      }

      fft.run(real, imaginary, true);
      for (let quefrency = lifterCutoff; quefrency <= size - lifterCutoff; quefrency++) {
        real[quefrency] = 0;
        imaginary[quefrency] = 0;
      }
      fft.run(real, imaginary, false);
      for (let bin = 0; bin <= half; bin++) envelope[bin] = real[bin]!;

      for (let bin = 0; bin <= half; bin++) {
        const sourceBin = bin / ratio;
        let shifted: number;
        if (sourceBin >= half) {
          shifted = envelope[half]!;
        } else {
          const lower = Math.floor(sourceBin);
          const upper = Math.min(lower + 1, half);
          const fraction = sourceBin - lower;
          shifted = envelope[lower]! + (envelope[upper]! - envelope[lower]!) * fraction;
        }
        // ±30 dB of correction is plenty; clamping stops a near-silent bin's log ratio
        // from exploding into a burst of noise.
        gainCurve[bin] = clamp(Math.exp(shifted - envelope[bin]!), 0.03, 32);
      }

      for (let bin = 0; bin < size; bin++) {
        const binGain = bin <= half ? gainCurve[bin]! : gainCurve[size - bin]!;
        real[bin] = spectrumReal[bin]! * binGain;
        imaginary[bin] = spectrumImaginary[bin]! * binGain;
      }
      fft.run(real, imaginary, true);

      for (let index = 0; index < size; index++) {
        const weight = window[index]!;
        output[position + index]! += real[index]! * weight;
        normalisation[position + index]! += weight * weight;
      }
      if (budget !== undefined && position % (hop * 16) === 0) await budget.tick();
    }

    for (let index = 0; index < frames; index++) {
      const weight = normalisation[leadIn + index]!;
      source[index] = weight > 1e-4 ? output[leadIn + index]! / weight : source[index]!;
    }
    if (budget !== undefined) await budget.tick();
  }
}

/**
 * Two pitch-shifted copies of the source, panned apart. Both are shifted from the original
 * rather than from each other, so a wide interval does not stack two lots of artefacts on
 * the upper voice.
 */
async function applyHarmoniser(
  channels: Float32Array[],
  options: {
    voice1: number;
    voice2: number;
    spread: number;
    mix: number;
    sampleRate: number;
    budget: RenderBudget | undefined;
  },
): Promise<Float32Array[]> {
  const frames = frameCount(channels);
  if (frames === 0 || options.mix <= 0.0001) return channels;
  if (Math.abs(options.voice1) <= 0.001 && Math.abs(options.voice2) <= 0.001) return channels;

  const first = await pitchShift(channels, options.voice1 * 100, options.sampleRate, options.budget);
  const second = await pitchShift(channels, options.voice2 * 100, options.sampleRate, options.budget);
  const width = clamp(options.spread, 0, 1);
  const firstPan = panGains(-width);
  const secondPan = panGains(width);
  const isStereo = channels.length >= 2;

  for (let channelIndex = 0; channelIndex < channels.length; channelIndex++) {
    const channel = channels[channelIndex]!;
    const firstVoice = first[channelIndex];
    const secondVoice = second[channelIndex];
    // A mono buffer has nowhere to place the voices, and widening it here would change the
    // channel count the rest of the render pass depends on.
    const firstGain = isStereo ? (channelIndex % 2 === 0 ? firstPan.left : firstPan.right) : 0.707;
    const secondGain = isStereo ? (channelIndex % 2 === 0 ? secondPan.left : secondPan.right) : 0.707;

    await forEachBlock(frames, BLOCK, options.budget, (from, to) => {
      for (let index = from; index < to; index++) {
        const dry = channel[index]!;
        const a = firstVoice !== undefined && index < firstVoice.length ? firstVoice[index]! : 0;
        const b = secondVoice !== undefined && index < secondVoice.length ? secondVoice[index]! : 0;
        channel[index] = dry * (1 - options.mix) + (a * firstGain + b * secondGain) * options.mix;
      }
    });
  }
  return channels;
}

/** Scale degrees: 0 chromatic, 1 major, 2 natural minor. */
function scaleDegrees(scale: number): readonly number[] {
  if (scale === 1) return [0, 2, 4, 5, 7, 9, 11];
  if (scale === 2) return [0, 2, 3, 5, 7, 8, 10];
  return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
}

function snapToScale(midi: number, root: number, degrees: readonly number[]): number {
  if (!Number.isFinite(midi)) return midi;
  const centre = Math.round(midi);
  let best = midi;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let candidate = centre - 6; candidate <= centre + 6; candidate++) {
    const pitchClass = (((candidate - root) % 12) + 12) % 12;
    if (!degrees.includes(pitchClass)) continue;
    const distance = Math.abs(midi - candidate);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }
  return best;
}

/**
 * YIN fundamental estimate for one window, or `null` when the window is unvoiced. The
 * cumulative mean normalised difference is what makes YIN robust where plain
 * autocorrelation picks the octave below.
 */
function detectPitch(samples: Float32Array, start: number, windowSize: number, sampleRate: number): number | null {
  const half = windowSize >> 1;
  if (start < 0 || start + windowSize > samples.length || half <= 8) return null;

  const minimumTau = Math.max(2, Math.round(sampleRate / 1200));
  const maximumTau = Math.min(half - 1, Math.round(sampleRate / 60));
  if (maximumTau <= minimumTau + 2) return null;

  let energy = 0;
  for (let index = 0; index < half; index++) {
    const value = samples[start + index]!;
    energy += value * value;
  }
  if (energy <= 1e-8) return null;

  const normalised = new Float64Array(maximumTau + 1).fill(1);
  let running = 0;
  for (let tau = 1; tau <= maximumTau; tau++) {
    let sum = 0;
    for (let index = 0; index < half; index++) {
      const delta = samples[start + index]! - samples[start + index + tau]!;
      sum += delta * delta;
    }
    running += sum;
    normalised[tau] = running > 0 ? (sum * tau) / running : 1;
  }

  const threshold = 0.15;
  let chosen = -1;
  for (let tau = minimumTau; tau <= maximumTau; tau++) {
    if (normalised[tau]! < threshold) {
      while (tau + 1 <= maximumTau && normalised[tau + 1]! < normalised[tau]!) tau++;
      chosen = tau;
      break;
    }
  }
  if (chosen < 0) {
    let lowest = Number.POSITIVE_INFINITY;
    for (let candidate = minimumTau; candidate <= maximumTau; candidate++) {
      if (normalised[candidate]! < lowest) {
        lowest = normalised[candidate]!;
        chosen = candidate;
      }
    }
    // Anything this aperiodic is a consonant or a breath, not a note.
    if (lowest >= 0.3 || chosen <= 0) return null;
  }

  let refined = chosen;
  if (chosen > minimumTau && chosen < maximumTau) {
    const previous = normalised[chosen - 1]!;
    const current = normalised[chosen]!;
    const next = normalised[chosen + 1]!;
    const denominator = 2 * (2 * current - previous - next);
    if (Math.abs(denominator) > 1e-12) refined += (next - previous) / denominator;
  }
  if (refined <= 0) return null;
  const frequency = sampleRate / refined;
  return Number.isFinite(frequency) && frequency > 50 && frequency < 1500 ? frequency : null;
}

function sampledTrack(track: Float64Array, frame: number, hop: number, centreOffset: number): number {
  const position = (frame - centreOffset) / hop;
  if (position <= 0) return track[0]!;
  if (position >= track.length - 1) return track[track.length - 1]!;
  const lower = Math.floor(position);
  const upper = Math.min(lower + 1, track.length - 1);
  return track[lower]! + (track[upper]! - track[lower]!) * (position - lower);
}

/**
 * Pitch correction: YIN tracking into scale-quantised, glide-limited PSOLA.
 *
 * `strength` scales the correction in cents rather than crossfading corrected against
 * uncorrected audio, so half strength means half the error removed — which is what a
 * "strength" control is expected to do.
 */
async function applyAutoTune(
  channels: Float32Array[],
  options: {
    strength: number;
    speed: number;
    root: number;
    scale: number;
    sampleRate: number;
    budget: RenderBudget | undefined;
  },
): Promise<void> {
  const frames = frameCount(channels);
  const windowSize = 2048;
  const hop = 512;
  if (frames <= windowSize || options.strength <= 0.001) return;

  const mono = new Float32Array(frames);
  const channelScale = 1 / channels.length;
  for (const channel of channels) {
    for (let index = 0; index < frames; index++) mono[index]! += channel[index]! * channelScale;
  }

  const degrees = scaleDegrees(options.scale);
  const rootPitchClass = ((options.root % 12) + 12) % 12;
  const hopSeconds = hop / options.sampleRate;
  const glideSeconds = 0.005 + (1 - clamp(options.speed, 0, 1)) * 0.245;
  const glideCoefficient = 1 - Math.exp(-hopSeconds / glideSeconds);

  const shortestPeriod = options.sampleRate / 1200;
  const longestPeriod = options.sampleRate / 60;
  const unvoicedPeriod = clamp(options.sampleRate / 150, shortestPeriod, longestPeriod);

  const analysisCount = Math.floor((frames - windowSize) / hop) + 1;
  const ratios = new Float64Array(analysisCount);
  const periods = new Float64Array(analysisCount);
  let smoothedCents = 0;

  for (let index = 0; index < analysisCount; index++) {
    let targetCents = 0;
    let period = unvoicedPeriod;
    const detected = detectPitch(mono, index * hop, windowSize, options.sampleRate);
    if (detected !== null) {
      period = clamp(options.sampleRate / detected, shortestPeriod, longestPeriod);
      const midi = 69 + 12 * Math.log2(detected / 440);
      const snapped = snapToScale(midi, rootPitchClass, degrees);
      targetCents = (snapped - midi) * 100 * clamp(options.strength, 0, 1);
    }
    smoothedCents += (targetCents - smoothedCents) * glideCoefficient;
    ratios[index] = Math.pow(2, smoothedCents / 1200);
    periods[index] = period;
    if (options.budget !== undefined && index % 32 === 0) await options.budget.tick();
  }
  if (analysisCount === 0) return;

  const centreOffset = windowSize / 2;
  // Pitch marks: one per period, walked forward from the start. A monophonic voice is
  // periodic enough that stepping by the tracked period lands on the same phase every
  // time, which is all the grain splice needs.
  const marks: number[] = [];
  let markPosition = 0;
  while (markPosition < frames) {
    marks.push(Math.floor(markPosition));
    markPosition += sampledTrack(periods, markPosition, hop, centreOffset);
  }
  if (marks.length <= 2) return;

  const table = hannWindow(1024);
  const tableScale = table.length - 1;

  for (let channelIndex = 0; channelIndex < channels.length; channelIndex++) {
    const source = channels[channelIndex]!.slice();
    const output = new Float64Array(frames);
    const weights = new Float64Array(frames);

    let markIndex = 0;
    let synthesis = 0;
    while (synthesis < frames) {
      const period = sampledTrack(periods, synthesis, hop, centreOffset);
      const ratio = sampledTrack(ratios, synthesis, hop, centreOffset);

      while (
        markIndex + 1 < marks.length &&
        Math.abs(marks[markIndex + 1]! - synthesis) < Math.abs(marks[markIndex]! - synthesis)
      ) {
        markIndex += 1;
      }

      const halfGrain = Math.max(4, Math.round(period));
      const grainLength = halfGrain * 2;
      const mark = marks[markIndex]!;
      const outputStart = Math.round(synthesis) - halfGrain;
      const indexScale = tableScale / grainLength;

      for (let index = 0; index < grainLength; index++) {
        const destination = outputStart + index;
        if (destination < 0 || destination >= frames) continue;
        // Where the grain sits relative to its own mark, measured in real time rather than
        // in whole samples.
        const origin = mark + destination - synthesis;
        if (origin < 0 || origin > frames - 1) continue;
        const weight = table[Math.round(index * indexScale)]!;
        output[destination]! += interpolateCatmullRom(source, origin) * weight;
        weights[destination]! += weight;
      }
      // Laying periods down closer together than they were read is exactly what raises the
      // pitch; the ratio is a bounded power of two so it never divides into zero.
      synthesis += period / ratio;
      if (options.budget !== undefined && marks.length % 256 === 0) await options.budget.tick();
    }

    const channel = channels[channelIndex]!;
    // Dividing by the accumulated window keeps the level right no matter how dense the
    // overlap became; the untouched source covers the edges the grains never reached.
    for (let index = 0; index < frames; index++) {
      channel[index] = weights[index]! > 1e-3 ? output[index]! / weights[index]! : source[index]!;
    }
    if (options.budget !== undefined) await options.budget.tick();
  }
}

/** Division index 0…5 in beats: 1/4, 1/8, 1/8T, 1/16, 1/16T, 1/32. */
const STUTTER_DIVISIONS = [1, 0.5, 1 / 3, 0.25, 1 / 6, 0.125];

/**
 * Beat-repeat. Every group of four slices plays the group's first slice four times, with a
 * gate on each repeat — the classic "hold the loop" stutter rather than a tremolo.
 */
async function applyStutter(
  channels: Float32Array[],
  options: {
    division: number;
    gateLength: number;
    mix: number;
    tempo: number;
    sampleRate: number;
    budget: RenderBudget | undefined;
  },
): Promise<void> {
  const frames = frameCount(channels);
  if (frames === 0 || options.mix <= 0.0001) return;

  const beats = STUTTER_DIVISIONS[clamp(options.division, 0, STUTTER_DIVISIONS.length - 1)]!;
  const beatSeconds = 60 / clamp(options.tempo, 20, 300);
  const sliceFrames = Math.max(32, Math.round(beats * beatSeconds * options.sampleRate));
  if (frames <= sliceFrames) return;

  const gate = clamp(options.gateLength, 0.05, 1);
  const gateFrames = Math.max(1, Math.round(sliceFrames * gate));
  const edgeFrames = Math.min(sliceFrames >> 3, Math.max(1, Math.round(0.002 * options.sampleRate)));
  const groupSlices = 4;

  for (const channel of channels) {
    const source = channel.slice();
    await forEachBlock(frames, BLOCK, options.budget, (from, to) => {
      for (let frame = from; frame < to; frame++) {
        const sliceIndex = Math.floor(frame / sliceFrames);
        const offset = frame - sliceIndex * sliceFrames;
        const groupStart = Math.floor(sliceIndex / groupSlices) * groupSlices;
        const sourceFrame = groupStart * sliceFrames + offset;

        let value = sourceFrame < frames ? source[sourceFrame]! : 0;
        if (offset >= gateFrames) value = 0;
        else if (offset >= gateFrames - edgeFrames) value *= (gateFrames - offset) / edgeFrames;
        else if (offset < edgeFrames) value *= offset / edgeFrames;

        channel[frame] = source[frame]! * (1 - options.mix) + value * options.mix;
      }
    });
  }
}

/**
 * Slice-level mangling: reverse, pitch jump, sub-slice repeat, and dropout.
 *
 * Every decision comes from one seeded generator walked in a fixed order, and the same
 * decisions are applied to every channel, so the result is reproducible and a stereo
 * source stays coherent instead of tearing into two different performances.
 */
async function applyGlitch(
  channels: Float32Array[],
  options: {
    density: number;
    sliceSize: number;
    reverseChance: number;
    pitchChance: number;
    seed: number;
    sampleRate: number;
    budget: RenderBudget | undefined;
  },
): Promise<void> {
  const frames = frameCount(channels);
  if (frames === 0 || options.density <= 0.0001) return;

  const sliceFrames = Math.max(64, Math.round(clamp(options.sliceSize, 0.01, 0.5) * options.sampleRate));
  if (frames <= sliceFrames) return;

  const sources = channels.map((channel) => channel.slice());
  const random = new SeededRandom(0, options.seed >>> 0);
  const edgeFrames = Math.min(sliceFrames >> 3, Math.max(1, Math.round(0.002 * options.sampleRate)));
  const pitchRates = [0.5, 0.75, 1.5, 2];

  for (let sliceStart = 0; sliceStart < frames; sliceStart += sliceFrames) {
    const sliceLength = Math.min(sliceFrames, frames - sliceStart);
    if (!random.chance(options.density)) continue;

    const reversed = random.chance(options.reverseChance);
    const rate = random.chance(options.pitchChance)
      ? pitchRates[Math.min(pitchRates.length - 1, Math.floor(random.unit() * pitchRates.length))]!
      : 1;
    const roll = random.unit();
    const repeatDivisor = roll < 0.25 ? (random.chance(0.5) ? 2 : 4) : 1;
    const isSilent = roll >= 0.25 && roll < 0.32;

    const unit = Math.max(8, Math.floor(sliceLength / repeatDivisor));
    const span = sliceLength;

    for (let channelIndex = 0; channelIndex < channels.length; channelIndex++) {
      const channel = channels[channelIndex]!;
      const source = sources[channelIndex]!;
      for (let index = 0; index < sliceLength; index++) {
        let value = 0;
        if (!isSilent) {
          let position = (index % unit) * rate;
          position %= span;
          if (reversed) position = span - 1 - position;
          value = interpolateCatmullRom(source, sliceStart + position);
        }
        if (index < edgeFrames) value *= index / edgeFrames;
        else if (index >= sliceLength - edgeFrames) value *= (sliceLength - index) / edgeFrames;
        channel[sliceStart + index] = value;
      }
    }
    if (options.budget !== undefined) await options.budget.tick();
  }
}

// MARK: - Rendering one edit

export interface RenderEditOptions {
  tempo?: number;
  /** 0 or omitted keeps the source rate. */
  targetSampleRate?: number;
  budget?: RenderBudget;
  /** Used only in error sentences, so the user knows which sound failed. */
  sourceName?: string;
}

export function toRenderedAudio(source: AudioBuffer | RenderedAudio): RenderedAudio {
  if ('channels' in source) return source;
  const channels: Float32Array[] = [];
  for (let index = 0; index < source.numberOfChannels; index++) {
    channels.push(source.getChannelData(index).slice());
  }
  return { channels, sampleRate: source.sampleRate };
}

function frameIndex(seconds: number, rate: number, limit: number): number {
  if (!Number.isFinite(seconds)) return 0;
  return clamp(Math.round(seconds * rate), 0, limit);
}

function padded(channels: Float32Array[], byFrames: number): Float32Array[] {
  if (byFrames <= 0) return channels;
  return channels.map((channel) => {
    const grown = new Float32Array(channel.length + byFrames);
    grown.set(channel);
    return grown;
  });
}

/**
 * Renders one non-destructive edit.
 *
 * **The order of operations below is contractual.** The wave editor's A/B compare, the
 * realtime pad chain and this renderer all have to agree, or a bounce would sound
 * different from what the user just auditioned. It is the same order as
 * `RenderService.renderEdit` on iOS:
 *
 * 1. trim to `startTime`…`endTime`
 * 2. silence every silenced region
 * 3. reverse
 * 4. playback rate — a stretch when the edit preserves pitch, a resample otherwise
 * 5. pitch by the total cents
 * 6. normalise
 * 7. static gain
 * 8. amp envelope
 * 9. fade in, then fade out
 * 10. loop crossfade
 * 11. the effect chain
 * 12. pan into stereo
 * 13. resample to the target rate
 *
 * Steps 1–3 are measured against the *source* timeline; everything from step 4 on is
 * measured against the rate-changed result, which is why the fade and crossfade lengths
 * are divided by the playback rate before they are applied.
 *
 * An identity edit passes through untouched — including its channel count, which is why a
 * mono file is only widened to stereo when the edit actually pans it.
 */
export async function renderEdit(
  source: AudioBuffer | RenderedAudio,
  edit: SampleEdit,
  options: RenderEditOptions = {},
): Promise<RenderedAudio> {
  const input = toRenderedAudio(source);
  const sourceRate = input.sampleRate;
  const sourceFrames = frameCount(input.channels);
  const name = options.sourceName ?? 'this sound';
  const budget = options.budget;
  const tempo = clamp(options.tempo ?? 120, 20, 300);

  if (!(sourceRate > 0) || sourceFrames === 0) {
    throw new RenderError(`“${name}” contains no audio. Re-import the sound, then try again.`);
  }

  const safeEdit: SampleEdit = clampSampleEdit(
    { ...edit, chain: { ...edit.chain, nodes: edit.chain.nodes.map((node) => ({ ...node })) } },
    sourceFrames / sourceRate,
  );

  // 1. Trim.
  const regionEndSeconds = safeEdit.endTime ?? sourceFrames / sourceRate;
  const startFrame = frameIndex(safeEdit.startTime, sourceRate, sourceFrames);
  const endFrame = Math.max(startFrame + 1, frameIndex(regionEndSeconds, sourceRate, sourceFrames));
  let channels =
    startFrame > 0 || endFrame < sourceFrames
      ? trimChannels(input.channels, startFrame, endFrame)
      : copyChannels(input.channels);

  const trimmedFrames = frameCount(channels);
  if (trimmedFrames === 0) {
    throw new RenderError(
      `The trimmed region of “${name}” is empty. Widen the selection, then try again.`,
    );
  }
  await budget?.tick();

  // 2. Silence. Regions are stored in source seconds, so they shift by the trim.
  if (safeEdit.silencedRegions.length > 0) {
    const fadeSamples = Math.max(1, Math.round(0.002 * sourceRate));
    for (const region of safeEdit.silencedRegions) {
      const lower = frameIndex(region.lowerBound, sourceRate, sourceFrames) - startFrame;
      const upper = frameIndex(region.upperBound, sourceRate, sourceFrames) - startFrame;
      silenceRegion(channels, clamp(lower, 0, trimmedFrames), clamp(upper, 0, trimmedFrames), fadeSamples);
    }
    await budget?.tick();
  }

  // 3. Reverse.
  if (safeEdit.isReversed) {
    reverseChannels(channels);
    await budget?.tick();
  }

  // 4. Playback rate. Both paths shorten the buffer by the same factor; only the pitch
  //    differs, so everything downstream can assume one scale factor.
  const rate = clamp(safeEdit.playbackRate, 0.25, 4);
  if (Math.abs(rate - 1) > 0.0001) {
    channels = safeEdit.preservesPitchWhenStretched
      ? await timeStretch(channels, rate, sourceRate, budget)
      : await resample(channels, sourceRate, sourceRate / rate, budget);
  }
  const ratedFrames = frameCount(channels);
  if (ratedFrames === 0) {
    throw new RenderError(
      `Changing the speed of “${name}” produced no audio. Reset the speed, then try again.`,
    );
  }

  // 5. Pitch.
  const totalCents = safeEdit.pitchSemitones * 100 + safeEdit.pitchCents;
  if (Math.abs(totalCents) > 0.01) {
    channels = await pitchShift(channels, totalCents, sourceRate, budget);
  }

  // 6. Normalise.
  if (safeEdit.isNormalised) normalizeChannels(channels, safeEdit.normalisationTargetDB);

  // 7. Static gain.
  applyGain(channels, safeEdit.gainDB);
  await budget?.tick();

  // 8. Amp envelope. Envelope times are absolute — a 10 ms attack stays 10 ms after a speed
  //    change, the way a synth envelope behaves — so they are not rate-scaled.
  if (safeEdit.envelope !== undefined) {
    applyAmpEnvelope(channels, safeEdit.envelope, sourceRate);
  }

  // 9. Fades. Stored against the source region, so they scale with the rate change and keep
  //    covering the same musical part of the clip.
  if (safeEdit.fadeInDuration > 0) {
    applyFadeIn(channels, safeEdit.fadeInDuration / rate, sourceRate, safeEdit.fadeCurve);
  }
  if (safeEdit.fadeOutDuration > 0) {
    applyFadeOut(channels, safeEdit.fadeOutDuration / rate, sourceRate, safeEdit.fadeCurve);
  }

  // 10. Loop seam.
  if (safeEdit.loopEnabled && safeEdit.loopCrossfade > 0) {
    const crossfade = Math.round((safeEdit.loopCrossfade / rate) * sourceRate);
    if (crossfade > 0) crossfadeLoop(channels, Math.min(crossfade, Math.floor(ratedFrames / 2)));
  }
  await budget?.tick();

  // 11. Effects. A reverb or delay needs somewhere to decay into, otherwise the bounce ends
  //     mid-tail while the live pad rings on.
  if (effectChainActiveNodes(safeEdit.chain).length > 0) {
    const tail = chainTailSeconds(safeEdit.chain);
    if (tail > 0) channels = padded(channels, Math.round(tail * sourceRate));
    channels = await applyEffectChain(channels, safeEdit.chain, {
      sampleRate: sourceRate,
      tempo,
      ...(budget !== undefined ? { budget } : {}),
    });
  }

  // 12. Pan. Only widen a mono source when the edit actually moves it, so an identity
  //     render of a mono file stays mono.
  if (Math.abs(safeEdit.pan) > 0.0001) {
    const stereo = makeStereo(channels);
    const gains = panGains(safeEdit.pan);
    // Constant-power pan sits at 0.707 in the centre; normalising by √2 keeps a centred
    // edit at unity so panning never changes the perceived level.
    const normalise = Math.SQRT2;
    scaleChannels([stereo[0]!], gains.left * normalise);
    scaleChannels([stereo[1]!], gains.right * normalise);
    channels = stereo;
  }

  // 13. Target rate.
  const targetRate = options.targetSampleRate ?? 0;
  if (targetRate > 0 && Math.abs(targetRate - sourceRate) > 0.5) {
    channels = await resample(channels, sourceRate, targetRate, budget);
    return { channels, sampleRate: targetRate };
  }
  return { channels, sampleRate: sourceRate };
}

// MARK: - Pattern timeline

interface RenderSegment {
  pattern: Pattern;
  /** How many times this pattern has already played, so polyrhythmic tracks keep phase. */
  pass: number;
}

/**
 * `forceSong` walks the arrangement whether or not the live song toggle happens to be on.
 *
 * Exporting the arrangement is a decision the user makes in the export sheet; requiring them
 * to have also flipped a transport switch on another screen makes "Complete Song" mean "the
 * song, if you happened to leave song mode armed", which is not a thing anyone can discover.
 */
export function renderTimeline(
  project: Project,
  patternID: UUID | undefined,
  passes: number,
  forceSong = false,
): RenderSegment[] {
  const segments: RenderSegment[] = [];
  const total = Math.max(1, passes);

  if (patternID === undefined && (forceSong || project.song.isEnabled) && project.song.sections.length > 0) {
    const passCounts = new Map<UUID, number>();
    for (let repeat = 0; repeat < total; repeat++) {
      for (const section of project.song.sections) {
        const pattern = project.patterns.find((entry) => entry.id === section.patternID);
        if (pattern === undefined) continue;
        for (let index = 0; index < Math.max(1, section.repeatCount); index++) {
          const pass = passCounts.get(pattern.id) ?? 0;
          segments.push({ pattern, pass });
          passCounts.set(pattern.id, pass + 1);
        }
      }
    }
  } else {
    const resolved =
      patternID !== undefined
        ? (project.patterns.find((entry) => entry.id === patternID) ?? project.patterns[0])
        : project.patterns[0];
    if (resolved === undefined) {
      throw new RenderError('This project has no patterns. Create one in the sequencer, then export again.');
    }
    for (let pass = 0; pass < total; pass++) segments.push({ pattern: resolved, pass });
  }

  if (segments.length === 0) {
    throw new RenderError('The song arrangement is empty. Add a section in the sequencer, then export again.');
  }
  return segments;
}

/**
 * Pads that actually sound in a render scope, in board order. This is the set
 * `renderStems` turns into files, so it applies the same mute, solo and empty-pad rules
 * the mixdown does.
 */
export function padIDsUsed(project: Project, patternID?: UUID): UUID[] {
  let segments: RenderSegment[];
  try {
    segments = renderTimeline(project, patternID, 1);
  } catch {
    return [];
  }
  const padsByID = new Map(projectAllPads(project).map((pad) => [pad.id, pad] as const));
  const soloedPadIDs = new Set(projectAllPads(project).filter((pad) => pad.isSoloed).map((pad) => pad.id));

  const seen = new Set<UUID>();
  const ordered: UUID[] = [];
  for (const segment of segments) {
    const hasTrackSolo = segment.pattern.tracks.some((track) => track.isSoloed);
    for (const track of segment.pattern.tracks) {
      const padID = track.padID;
      if (padID === undefined) continue;
      const pad = padsByID.get(padID);
      if (pad === undefined || pad.assetID === undefined) continue;
      if (track.isMuted || (hasTrackSolo && !track.isSoloed)) continue;
      if (pad.isMuted) continue;
      if (soloedPadIDs.size > 0 && !soloedPadIDs.has(padID)) continue;
      if (trackActiveStepCount(track) === 0) continue;
      if (!seen.has(padID)) {
        seen.add(padID);
        ordered.push(padID);
      }
    }
  }
  return ordered;
}

interface ScheduledHit {
  padID: UUID;
  trackID: UUID;
  key: string;
  pitchOffset: number;
  gain: number;
  pan: number;
  frame: number;
  /** Scheduling order, so two hits on the same frame keep a fixed order and the export
   *  stays reproducible. */
  order: number;
  chokeGroup: number | undefined;
  chokesPrevious: boolean;
  retrigger: RetriggerBehaviour;
  releaseFade: number;
}

/** Decoded source audio for an asset. Implemented by the media layer. */
export interface AssetAudioSource {
  /**
   * Resolves with the decoded source file. Rejects with a `RenderError` whose message
   * names the sound when the file is missing or will not decode.
   */
  load(asset: SampleAsset): Promise<AudioBuffer | RenderedAudio>;
}

export interface RenderPatternOptions {
  media: AssetAudioSource;
  patternID?: UUID;
  /** Render `project.song` rather than one pattern, regardless of the live song toggle. */
  song?: boolean;
  repeats?: number;
  sampleRate?: number;
  isolatePadID?: UUID;
  signal?: AbortSignal;
  onProgress?: (fraction: number) => void;
}

/** A render longer than this is almost certainly a mistake, and would exhaust memory. */
const MAXIMUM_RENDER_SECONDS = 15 * 60;

interface PatternRenderState {
  events: ScheduledHit[];
  buffers: Map<string, RenderedAudio>;
  cuts: Map<number, number>;
  dropped: Set<number>;
  totalFrames: number;
  masterCurve: Array<{ frame: number; value: number }>;
  hasMasterAutomation: boolean;
}

function automationLaneFor(
  pattern: Pattern,
  match: (lane: AutomationLane) => boolean,
): AutomationLane | undefined {
  return pattern.automation.find((lane) => lane.isEnabled && lane.points.length > 0 && match(lane));
}

async function planPattern(
  project: Project,
  options: RenderPatternOptions,
  sampleRate: number,
  budget: RenderBudget,
): Promise<PatternRenderState> {
  const segments = renderTimeline(
    project,
    options.song === true ? undefined : options.patternID,
    Math.max(1, options.repeats ?? 1),
    options.song === true,
  );
  const tempo = clamp(project.tempo, 20, 300);
  const secondsPerBeat = 60 / tempo;
  const staticMasterVolume = faderTaper(clamp(project.master.volume, 0, 1));

  const padsByID = new Map(projectAllPads(project).map((pad) => [pad.id, pad] as const));
  const soloedPadIDs = new Set(projectAllPads(project).filter((pad) => pad.isSoloed).map((pad) => pad.id));

  const events: ScheduledHit[] = [];
  const masterCurve: Array<{ frame: number; value: number }> = [];
  let hasMasterAutomation = false;
  const randoms = new Map<UUID, SeededRandom>();
  let cursorSeconds = 0;

  for (const segment of segments) {
    budget.throwIfCancelled();
    const pattern = segment.pattern;
    const stepsPerBeat = Math.max(1, pattern.stepsPerBeat);
    const secondsPerStep = secondsPerBeat / stepsPerBeat;
    const segmentSeconds = pattern.length * secondsPerStep;
    const swing = clamp(pattern.swing ?? project.swing, 0, 0.75);
    const hasTrackSolo = pattern.tracks.some((track) => track.isSoloed);

    const masterLane = automationLaneFor(pattern, (lane) => 'masterVolume' in lane.target);
    if (masterLane !== undefined) hasMasterAutomation = true;
    const resolution = Math.max(2, pattern.length * 4);
    for (let index = 0; index <= resolution; index++) {
      const offsetSeconds = (index / resolution) * segmentSeconds;
      const raw =
        masterLane !== undefined
          ? (automationValueAtBeat(masterLane, offsetSeconds / secondsPerBeat) ?? staticMasterVolume)
          : staticMasterVolume;
      masterCurve.push({
        frame: Math.round((cursorSeconds + offsetSeconds) * sampleRate),
        value: faderTaper(clamp(raw, 0, 1)),
      });
    }

    for (const track of pattern.tracks) {
      const padID = track.padID;
      if (padID === undefined) continue;
      const pad = padsByID.get(padID);
      if (pad === undefined || pad.assetID === undefined) continue;
      // Silence decisions are made per track, and each track owns its own random substream,
      // so muting one track never reshuffles another one's rolls. That is what keeps a stem
      // identical to its share of the full mix.
      if (track.isMuted) continue;
      if (hasTrackSolo && !track.isSoloed) continue;
      if (pad.isMuted) continue;
      if (soloedPadIDs.size > 0 && !soloedPadIDs.has(padID)) continue;

      let random = randoms.get(track.id);
      if (random === undefined) {
        random = trackRandom(pattern.id, track.id);
        randoms.set(track.id, random);
      }

      for (let stepInPass = 0; stepInPass < pattern.length; stepInPass++) {
        const globalPosition = segment.pass * pattern.length + stepInPass;
        const step = trackStepAtGlobalPosition(track, globalPosition);
        if (step === undefined || !step.isActive) continue;

        // A loop track fires once per pass, from its earliest lit step — the bed spans the
        // pattern rather than sitting in a cell of it. Skipped *before* the probability draw
        // so the seeded stream advances in exactly the same places the live scheduler
        // advances it (`app/state.ts`), which is what keeps a bounce identical to what was
        // auditioned.
        if (track.oneHitPerPass === true) {
          const span = Math.max(1, track.length);
          if (((globalPosition % span) + span) % span !== trackLeadingStepIndex(track)) continue;
        }

        // One draw per active step, even at probability 1. Turning a step's probability
        // down later then changes only that step instead of re-rolling every step after it.
        const roll = random.unit();
        if (step.probability < 0.999 && roll >= step.probability) continue;

        const swungOffset = globalPosition % 2 === 0 ? 0 : swing * secondsPerStep * 0.5;
        const microOffset = step.microTiming * secondsPerStep;
        const base = cursorSeconds + stepInPass * secondsPerStep + swungOffset + microOffset;

        const beatInPattern = stepInPass / stepsPerBeat;
        let volume = pad.volume;
        let pan = pad.pan;
        for (const lane of pattern.automation) {
          if (!lane.isEnabled) continue;
          if ('padVolume' in lane.target && lane.target.padVolume.padID === padID) {
            const value = automationValueAtBeat(lane, beatInPattern);
            if (value !== undefined) volume = clamp(value, 0, 1);
          } else if ('padPan' in lane.target && lane.target.padPan.padID === padID) {
            const value = automationValueAtBeat(lane, beatInPattern);
            if (value !== undefined) pan = clamp(value, -1, 1);
          }
        }

        // A loop track never ratchets: there is no step to subdivide when the sound spans
        // the whole pattern. Both schedulers make the same substitution.
        const ratchets = track.oneHitPerPass === true ? 1 : clamp(Math.round(step.ratchets), 1, 8);
        const ratchetSpacing = secondsPerStep / ratchets;
        const pitchOffset = clamp(step.pitchOffset, -24, 24);
        for (let ratchet = 0; ratchet < ratchets; ratchet++) {
          const seconds = Math.max(0, base + ratchet * ratchetSpacing);
          events.push({
            padID,
            trackID: track.id,
            key: `${padID}:${Math.round(pitchOffset * 1000)}`,
            pitchOffset,
            gain: faderTaper(volume) * clamp(step.velocity, 0, 1),
            pan,
            frame: Math.round(seconds * sampleRate),
            order: events.length,
            chokeGroup: pad.chokeGroup,
            chokesPrevious: step.chokesPrevious,
            retrigger: pad.retrigger,
            releaseFade: pad.releaseFade,
          });
        }
      }
      await budget.tick();
    }
    cursorSeconds += segmentSeconds;
  }

  if (cursorSeconds > MAXIMUM_RENDER_SECONDS) {
    throw new RenderError(
      `This export would be ${Math.round(cursorSeconds / 60)} minutes long, which is more than PadLab can render in one pass. Reduce the number of repeats, then try again.`,
    );
  }

  const musicalFrames = Math.max(1, Math.round(cursorSeconds * sampleRate));
  events.sort((a, b) => (a.frame === b.frame ? a.order - b.order : a.frame - b.frame));

  return {
    events,
    buffers: new Map(),
    cuts: new Map(),
    dropped: new Set(),
    totalFrames: musicalFrames,
    masterCurve,
    hasMasterAutomation,
  };
}

/** Voice bookkeeping: which hits get cut short and which never sound at all. */
function resolveVoices(state: PatternRenderState, lengths: number[]): void {
  const soundingEnd = (index: number): number => {
    const natural = state.events[index]!.frame + (lengths[index] ?? 0);
    const cut = state.cuts.get(index);
    return cut === undefined ? natural : Math.min(cut, natural);
  };
  const cut = (index: number, frame: number): void => {
    const existing = state.cuts.get(index);
    state.cuts.set(index, existing === undefined ? frame : Math.min(existing, frame));
  };

  const lastByGroup = new Map<number, number>();
  const lastByTrack = new Map<UUID, number>();
  const lastByPad = new Map<UUID, number>();

  for (let index = 0; index < state.events.length; index++) {
    const event = state.events[index]!;

    // `ignore` swallows a retrigger while the pad is still sounding. `layer` and `resume`
    // both stack offline — there is no live voice to resume into.
    const previousOnPad = lastByPad.get(event.padID);
    if (
      event.retrigger === 'ignore' &&
      previousOnPad !== undefined &&
      !state.dropped.has(previousOnPad) &&
      soundingEnd(previousOnPad) > event.frame
    ) {
      state.dropped.add(index);
      continue;
    }

    // Hits landing on the exact same frame layer rather than choke each other, so a chord
    // programmed across a choke group still sounds as a chord.
    if (event.chokeGroup !== undefined) {
      const previous = lastByGroup.get(event.chokeGroup);
      if (previous !== undefined && !state.dropped.has(previous) && state.events[previous]!.frame < event.frame) {
        cut(previous, event.frame);
      }
    }
    if (event.chokesPrevious) {
      const previous = lastByTrack.get(event.trackID);
      if (previous !== undefined && !state.dropped.has(previous) && state.events[previous]!.frame < event.frame) {
        cut(previous, event.frame);
      }
    }
    if (event.retrigger === 'restart' && previousOnPad !== undefined && !state.dropped.has(previousOnPad)) {
      if (state.events[previousOnPad]!.frame < event.frame) cut(previousOnPad, event.frame);
    }

    if (event.chokeGroup !== undefined) lastByGroup.set(event.chokeGroup, index);
    lastByTrack.set(event.trackID, index);
    lastByPad.set(event.padID, index);
  }
}

function makeAudioBuffer(context: BaseAudioContext, audio: RenderedAudio): AudioBuffer {
  const frames = Math.max(1, frameCount(audio.channels));
  const buffer = context.createBuffer(Math.max(1, audio.channels.length), frames, audio.sampleRate);
  for (let index = 0; index < audio.channels.length; index++) {
    // `getChannelData().set()` rather than `copyToChannel()`: it writes straight into the
    // buffer's own storage instead of asking the engine to copy from a view whose backing
    // store the type system cannot prove is a plain ArrayBuffer.
    buffer.getChannelData(index).set(audio.channels[index]!.subarray(0, frames));
  }
  return buffer;
}

/**
 * A pan stage. `StereoPannerNode` implements exactly the rule the native mixer uses —
 * equal-power for a mono source, balance for a stereo one — so it is the first choice; the
 * fallback reproduces the same two rules by hand for engines that predate it.
 */
function makePanStage(
  context: BaseAudioContext,
  pan: number,
  channelCount: number,
): { input: AudioNode; output: AudioNode } {
  const factory = (context as BaseAudioContext & { createStereoPanner?: () => StereoPannerNode })
    .createStereoPanner;
  if (typeof factory === 'function') {
    const panner = context.createStereoPanner();
    panner.pan.value = clamp(pan, -1, 1);
    return { input: panner, output: panner };
  }

  const gains = panGains(pan);
  const merger = context.createChannelMerger(2);
  if (channelCount < 2) {
    const left = context.createGain();
    const right = context.createGain();
    left.gain.value = gains.left;
    right.gain.value = gains.right;
    left.connect(merger, 0, 0);
    right.connect(merger, 0, 1);
    const input = context.createGain();
    input.connect(left);
    input.connect(right);
    return { input, output: merger };
  }

  // Stereo balance: centre leaves the image alone, moving the control attenuates the far
  // side. Constant-power gains are normalised so centre stays at unity.
  const splitter = context.createChannelSplitter(2);
  const left = context.createGain();
  const right = context.createGain();
  left.gain.value = gains.left * Math.SQRT2;
  right.gain.value = gains.right * Math.SQRT2;
  splitter.connect(left, 0);
  splitter.connect(right, 1);
  left.connect(merger, 0, 0);
  right.connect(merger, 0, 1);
  return { input: splitter, output: merger };
}

function offlineContextFor(channels: number, frames: number, sampleRate: number): OfflineAudioContext {
  if (typeof OfflineAudioContext !== 'function') {
    throw new RenderError(
      'This browser cannot render audio offline, so PadLab cannot export a mix here. Open PadLab in Safari or Chrome and try again.',
    );
  }
  return new OfflineAudioContext(channels, frames, sampleRate);
}

async function renderPatternInternal(
  project: Project,
  options: RenderPatternOptions,
  state: PatternRenderState,
  sampleRate: number,
  budget: RenderBudget,
): Promise<RenderedAudio> {
  const tempo = clamp(project.tempo, 20, 300);
  const isStem = options.isolatePadID !== undefined;

  const lengths = state.events.map((event) => frameCount(state.buffers.get(event.key)?.channels ?? []));
  state.cuts.clear();
  state.dropped.clear();
  resolveVoices(state, lengths);

  // The mix runs until the last voice has finished, plus whatever the master chain needs to
  // decay, plus a beat of slack for the limiter release.
  let lastAudioFrame = state.totalFrames;
  for (let index = 0; index < state.events.length; index++) {
    if (state.dropped.has(index)) continue;
    const natural = state.events[index]!.frame + (lengths[index] ?? 0);
    const cut = state.cuts.get(index);
    lastAudioFrame = Math.max(lastAudioFrame, cut === undefined ? natural : Math.min(cut, natural));
  }
  const masterTail = Math.round(chainTailSeconds(project.master.chain) * sampleRate);
  const totalFrames = Math.max(1, lastAudioFrame + masterTail + Math.round(0.05 * sampleRate));

  const context = offlineContextFor(2, totalFrames, sampleRate);
  const master = context.createGain();
  master.gain.value = 1;
  master.connect(context.destination);

  let scheduled = 0;
  for (let index = 0; index < state.events.length; index++) {
    if (state.dropped.has(index)) continue;
    const event = state.events[index]!;
    if (options.isolatePadID !== undefined && event.padID !== options.isolatePadID) continue;
    const audio = state.buffers.get(event.key);
    if (audio === undefined) continue;
    const sourceFrames = frameCount(audio.channels);
    if (sourceFrames === 0) continue;

    const startTime = event.frame / sampleRate;
    const node = context.createBufferSource();
    node.buffer = makeAudioBuffer(context, audio);

    const gain = context.createGain();
    gain.gain.value = event.gain;
    const panStage = makePanStage(context, event.pan, audio.channels.length);
    node.connect(gain);
    gain.connect(panStage.input);
    panStage.output.connect(master);

    const cutFrame = state.cuts.get(index);
    if (cutFrame !== undefined) {
      const usable = clamp(cutFrame - event.frame, 0, sourceFrames);
      if (usable <= 0) continue;
      if (usable < sourceFrames) {
        // Fade the choked voice out over its release time so the old sound ends as the new
        // one starts, rather than being cut off mid-cycle with a click.
        const fade = clamp(event.releaseFade, 0.002, usable / sampleRate);
        const stopTime = startTime + usable / sampleRate;
        gain.gain.setValueAtTime(event.gain, Math.max(0, stopTime - fade));
        gain.gain.linearRampToValueAtTime(0, stopTime);
        node.start(startTime);
        node.stop(stopTime);
        scheduled++;
        continue;
      }
    }
    node.start(startTime);
    scheduled++;
    if (scheduled % 128 === 0) await budget.tick();
  }

  // Master volume rides its automation curve; without automation it is one constant gain.
  if (state.hasMasterAutomation && state.masterCurve.length >= 2) {
    for (const point of state.masterCurve) {
      const time = clamp(point.frame / sampleRate, 0, totalFrames / sampleRate);
      master.gain.linearRampToValueAtTime(point.value, time);
    }
  } else {
    master.gain.value = faderTaper(clamp(project.master.volume, 0, 1));
  }

  budget.report(0.6);
  const rendered = await context.startRendering();
  budget.throwIfCancelled();
  budget.report(0.8);

  const mix: Float32Array[] = [];
  for (let index = 0; index < rendered.numberOfChannels; index++) {
    mix.push(rendered.getChannelData(index).slice());
  }

  // A stem is one pad's contribution to the mix, so it stops short of the master bus. The
  // master chain and the limiter are bus-wide and mostly non-linear: running them once per
  // stem would process the same signal eight times over when the stems are summed back
  // together elsewhere. Master volume and headroom are plain gains, so they stay — that is
  // what keeps a stem at the level it had in the mix.
  if (!isStem && effectChainActiveNodes(project.master.chain).length > 0) {
    await applyEffectChain(mix, project.master.chain, { sampleRate, tempo, budget });
  }

  applyGain(mix, project.master.headroomDB);
  budget.report(0.9);

  if (!isStem && project.master.limiterEnabled) {
    await limit(mix, {
      ceilingDB: project.master.limiterCeilingDB,
      release: 0.08,
      driveDB: 0,
      sampleRate,
      budget,
    });
  }

  // Final ceiling guard, applied to stems too. The limiter should already have done this;
  // if a pathological chain still leaves a sample above the ceiling, a file written as
  // fixed-point PCM must not clip.
  const ceiling = dbToLinear(Math.min(project.master.limiterCeilingDB, 0));
  const peak = peakOf(mix);
  if (peak > ceiling && peak > 0) scaleChannels(mix, ceiling / peak);

  budget.report(1);
  return { channels: mix, sampleRate };
}

/** Renders every unique (pad, pitch) pair once — never once per hit. */
async function renderEventBuffers(
  project: Project,
  state: PatternRenderState,
  options: RenderPatternOptions,
  sampleRate: number,
  budget: RenderBudget,
): Promise<void> {
  const padsByID = new Map(projectAllPads(project).map((pad) => [pad.id, pad] as const));
  const assetsByID = new Map(project.assets.map((asset) => [asset.id, asset] as const));
  const tempo = clamp(project.tempo, 20, 300);

  const keys: Array<{ key: string; padID: UUID; pitchOffset: number }> = [];
  const seen = new Set<string>();
  for (const event of state.events) {
    if (seen.has(event.key) || state.buffers.has(event.key)) continue;
    seen.add(event.key);
    keys.push({ key: event.key, padID: event.padID, pitchOffset: event.pitchOffset });
  }

  const sources = new Map<UUID, RenderedAudio>();
  for (let index = 0; index < keys.length; index++) {
    budget.throwIfCancelled();
    const entry = keys[index]!;
    const pad = padsByID.get(entry.padID);
    const asset = pad?.assetID !== undefined ? assetsByID.get(pad.assetID) : undefined;
    if (pad === undefined || asset === undefined) continue;

    let source = sources.get(asset.id);
    if (source === undefined) {
      source = toRenderedAudio(await options.media.load(asset));
      sources.set(asset.id, source);
    }

    const edit: SampleEdit = {
      ...pad.edit,
      pitchSemitones: clamp(pad.edit.pitchSemitones + entry.pitchOffset, -24, 24),
    };
    state.buffers.set(
      entry.key,
      await renderEdit(source, edit, {
        tempo,
        targetSampleRate: sampleRate,
        budget,
        sourceName: asset.name,
      }),
    );
    budget.report((0.55 * (index + 1)) / keys.length);
  }
}

/**
 * Renders a pattern — or the whole song arrangement when no pattern is named and the
 * arrangement is enabled — into a stereo mix.
 */
export async function renderPattern(
  project: Project,
  options: RenderPatternOptions,
): Promise<RenderedAudio> {
  const sampleRate = clamp(options.sampleRate ?? 48000, 8000, 96000);
  const budget = new RenderBudget({
    ...(options.signal !== undefined ? { signal: options.signal } : {}),
    ...(options.onProgress !== undefined ? { onProgress: options.onProgress } : {}),
  });

  const finish = log.time('renderPattern');
  const state = await planPattern(project, options, sampleRate, budget);
  await renderEventBuffers(project, state, options, sampleRate, budget);
  const mix = await renderPatternInternal(project, options, state, sampleRate, budget);
  finish();
  return mix;
}

/**
 * One buffer per sounding pad. Every stem is planned from the same event list as the mix —
 * including the probability rolls and the choke decisions — so the stems sum back to the
 * mix instead of drifting apart.
 */
export async function renderStems(
  project: Project,
  options: RenderPatternOptions,
): Promise<StemRender[]> {
  const sampleRate = clamp(options.sampleRate ?? 48000, 8000, 96000);
  const budget = new RenderBudget({
    ...(options.signal !== undefined ? { signal: options.signal } : {}),
    ...(options.onProgress !== undefined ? { onProgress: options.onProgress } : {}),
  });

  const state = await planPattern(project, options, sampleRate, budget);
  await renderEventBuffers(project, state, options, sampleRate, budget);

  const padsByID = new Map(projectAllPads(project).map((pad) => [pad.id, pad] as const));
  const padIDs = padIDsUsed(project, options.patternID);
  const stems: StemRender[] = [];

  for (let index = 0; index < padIDs.length; index++) {
    const padID = padIDs[index]!;
    const pad = padsByID.get(padID);
    const audio = await renderPatternInternal(
      project,
      { ...options, isolatePadID: padID },
      state,
      sampleRate,
      budget,
    );
    stems.push({ padID, name: stemName(pad, index), audio });
    budget.report((index + 1) / padIDs.length);
  }
  return stems;
}

function stemName(pad: Pad | undefined, index: number): string {
  if (pad === undefined) return `Stem ${index + 1}`;
  if (pad.name.trim() !== '') return pad.name;
  return `Pad ${pad.slot + 1}`;
}

// MARK: - Encoding

export type WavBitDepth = 16 | 24 | 32;

/**
 * Writes a canonical RIFF/WAVE file. 16- and 24-bit are integer PCM (format 1); 32-bit is
 * IEEE float (format 3), which is what a lossless master wants — it cannot clip and needs
 * no dither.
 */
export function encodeWav(audio: RenderedAudio | AudioBuffer, bitDepth: WavBitDepth = 24): Blob {
  const input = toRenderedAudio(audio);
  const channels = input.channels.length > 0 ? input.channels : [new Float32Array(1)];
  const frames = Math.max(1, frameCount(channels));
  const channelCount = channels.length;
  const bytesPerSample = bitDepth / 8;
  const blockAlign = channelCount * bytesPerSample;
  const dataBytes = frames * blockAlign;
  const isFloat = bitDepth === 32;

  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);

  const writeText = (offset: number, text: string): void => {
    for (let index = 0; index < text.length; index++) view.setUint8(offset + index, text.charCodeAt(index));
  };

  writeText(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  writeText(8, 'WAVE');
  writeText(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, isFloat ? 3 : 1, true);
  view.setUint16(22, channelCount, true);
  view.setUint32(24, Math.round(input.sampleRate), true);
  view.setUint32(28, Math.round(input.sampleRate) * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeText(36, 'data');
  view.setUint32(40, dataBytes, true);

  let offset = 44;
  for (let frame = 0; frame < frames; frame++) {
    for (let channel = 0; channel < channelCount; channel++) {
      const sample = clamp(channels[channel]![frame] ?? 0, -1, 1);
      if (isFloat) {
        view.setFloat32(offset, sample, true);
        offset += 4;
      } else if (bitDepth === 24) {
        // Asymmetric scaling: full-scale negative is -2^23 and full-scale positive is
        // 2^23 - 1, so scaling by 2^23 and clamping is the only mapping that uses the
        // whole range without wrapping +1.0 round to the most negative value.
        const value = Math.max(-8388608, Math.min(8388607, Math.round(sample * 8388608)));
        const unsigned = value < 0 ? value + 0x1000000 : value;
        view.setUint8(offset, unsigned & 0xff);
        view.setUint8(offset + 1, (unsigned >> 8) & 0xff);
        view.setUint8(offset + 2, (unsigned >> 16) & 0xff);
        offset += 3;
      } else {
        const value = Math.max(-32768, Math.min(32767, Math.round(sample * 32768)));
        view.setInt16(offset, value, true);
        offset += 2;
      }
    }
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

/** Container types tried for AAC output, best first. */
const M4A_MIME_CANDIDATES = ['audio/mp4;codecs=mp4a.40.2', 'audio/mp4'];

/** The MIME type this browser can write AAC into, or `null` when it cannot write any. */
export function supportedM4AMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const candidate of M4A_MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return null;
}

export function isM4AExportSupported(): boolean {
  return supportedM4AMimeType() !== null;
}

export interface EncodeM4AOptions {
  bitsPerSecond?: number;
  signal?: AbortSignal;
  /** 0…1 through the encode. Reported roughly once a second. */
  onProgress?: (fraction: number) => void;
  /**
   * A resumed realtime context. `MediaStreamAudioDestinationNode` does not exist on an
   * `OfflineAudioContext`, so encoding needs a live one; passing the app's own context
   * avoids creating a second output device.
   */
  context?: AudioContext;
}

/**
 * Encodes to AAC in an MP4 container using `MediaRecorder` over a
 * `MediaStreamAudioDestinationNode`.
 *
 * **This runs in real time.** There is no non-realtime AAC encoder on the web platform
 * that ships in Safari — `AudioEncoder` (WebCodecs) has no MP4 muxer behind it, and no
 * encoder is exposed to `OfflineAudioContext` — so a three-minute mix takes three minutes
 * to encode. The caller is expected to show that honestly, which is why `onProgress` is
 * reported and `signal` is honoured throughout. WAV export is instant and lossless and is
 * the right default; M4A is for sharing.
 *
 * Throws `ExportFormatUnavailableError` when the browser cannot write MP4 at all, with a
 * message that names WAV as the alternative.
 */
export async function encodeM4A(
  audio: RenderedAudio | AudioBuffer,
  options: EncodeM4AOptions = {},
): Promise<Blob> {
  const mimeType = supportedM4AMimeType();
  if (mimeType === null) {
    throw new ExportFormatUnavailableError(
      'This browser cannot write M4A files. Export as WAV instead. It is lossless, and every app that opens M4A opens WAV too.',
    );
  }
  if (typeof AudioContext === 'undefined') {
    throw new ExportFormatUnavailableError(
      'This browser has no Web Audio support, so PadLab cannot export here. Open PadLab in Safari or Chrome and try again.',
    );
  }

  const input = toRenderedAudio(audio);
  const frames = Math.max(1, frameCount(input.channels));
  const duration = frames / input.sampleRate;

  const ownsContext = options.context === undefined;
  const context = options.context ?? new AudioContext({ sampleRate: input.sampleRate });
  if (context.state === 'suspended') await context.resume();

  const destination = context.createMediaStreamDestination();
  const source = context.createBufferSource();
  source.buffer = makeAudioBuffer(context, input);
  source.connect(destination);

  const recorderOptions: MediaRecorderOptions = { mimeType };
  if (options.bitsPerSecond !== undefined) recorderOptions.audioBitsPerSecond = options.bitsPerSecond;
  const recorder = new MediaRecorder(destination.stream, recorderOptions);
  const chunks: Blob[] = [];

  const startedAt = context.currentTime;
  const progressTimer =
    options.onProgress !== undefined
      ? setInterval(() => {
          options.onProgress?.(clamp((context.currentTime - startedAt) / duration, 0, 1));
        }, 500)
      : null;

  const cleanup = (): void => {
    if (progressTimer !== null) clearInterval(progressTimer);
    for (const track of destination.stream.getTracks()) track.stop();
    try {
      source.disconnect();
      destination.disconnect();
    } catch {
      // Disconnecting a node that never connected is not an error worth surfacing.
    }
    if (ownsContext) void context.close();
  };

  try {
    const blob = await new Promise<Blob>((resolve, reject) => {
      const abort = (): void => {
        try {
          if (recorder.state !== 'inactive') recorder.stop();
          source.stop();
        } catch {
          // Already stopped.
        }
        reject(new RenderCancelledError());
      };
      options.signal?.addEventListener('abort', abort, { once: true });

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      recorder.onerror = () => {
        reject(new RenderError('The M4A encoder stopped unexpectedly. Export as WAV, or try again.'));
      };
      recorder.onstop = () => {
        options.signal?.removeEventListener('abort', abort);
        resolve(new Blob(chunks, { type: mimeType }));
      };
      source.onended = () => {
        // A short trailing window lets the encoder flush its last frames; stopping on the
        // same tick truncates the final packet.
        setTimeout(() => {
          if (recorder.state !== 'inactive') recorder.stop();
        }, 120);
      };

      recorder.start(250);
      source.start();
    });
    options.onProgress?.(1);
    return blob;
  } finally {
    cleanup();
  }
}

/** Turns a render back into a playable buffer — the preview path for a bounce. */
export function renderedToAudioBuffer(context: BaseAudioContext, audio: RenderedAudio): AudioBuffer {
  return makeAudioBuffer(context, audio);
}

/** Total capacity of every bank, used by callers sizing an export. */
export function projectPadCapacity(project: Project): number {
  return project.banks.reduce((total, bank) => total + gridCapacity(bank.grid), 0);
}

/** Length of one pass of a pattern in seconds at the project tempo. */
export function patternDurationSeconds(project: Project, pattern: Pattern): number {
  return (patternLengthInBeats(pattern) * 60) / clamp(project.tempo, 20, 300);
}
